// ─────────────────────────────────────────────────────────────────────────────
// moon.js — The Stars Above / moon phase customiser
//
// A sibling of starmap.js. Everything around the artwork — location search, the
// 4-step wizard, sizes, formats, fonts, the modals, the cart and the print upload
// — is the same. The only real difference is what fills the poster's slot:
// MoonEngine.renderSquare() instead of a live celestial map.
//
// That makes this simpler than starmap.js: a moon is a finished image, so there's
// no live canvas to position in the slot, no zoom to keep in sync, and no star
// data to load. The moon square goes straight into the SVG.
// ─────────────────────────────────────────────────────────────────────────────

(function() {
  'use strict';

  // ─────────────────────────────────────────────────────────────────────────
  // Constants & State
  // ─────────────────────────────────────────────────────────────────────────

  var DATA_PATH = 'https://kgoyal000.github.io/StarmapWithLocation/data/';
  var MOBILE_BREAKPOINT = 768;
  // Mobile preview width cap. Raised from 260 so the poster fills about 75% of a
  // typical phone screen (closer to The Night Sky). Every use is min(available, cap),
  // so small phones still fit.
  var MOBILE_POSTER_MAX_WIDTH = 320;


  // Desktop size-change animation. One easing + duration shared by every moving part
  // (poster box, map wrapper, map canvas) so they glide together instead of the box
  // animating while the contents snap. Only a real size (ratio) change animates.
  var SIZE_DUR  = '0.5s';
  var SIZE_EASE = 'cubic-bezier(0.33, 0.0, 0.15, 1)';
  var lastRenderedRatio = null;

  var state = {
    lat: 51.5,
    lon: -0.12,
    locationName: 'London, United Kingdom',
    coordinates: '51.50° N, 0.12° W',
    size: '12" x 16"',
    format: 'Poster',
    frameColour: 'Black',
    printWidth: 3638,
    printHeight: 4819,
    gelatoUid: 'flat_300x400-mm-12x16-inch_200-gsm-80lb-uncoated_4-0_ver',
    date: new Date(),
    shape: 'circle',
    bgColor: '#0a0a0a',   // Obsidian — matches the swatch marked active in the section
    starColor: '#ffffff',   // still drives the starfield colour
    showGlow: true,         // the soft halo behind the moon
    showStars: true,        // procedural starfield
    showBorder: false,
    showDetails: true,      // the PHASE | ILLUMINATION | MOON AGE strip
    name: '',
    message: '',
    font: "'Cormorant Garamond', serif",
    subheadingFont: "'Montserrat', sans-serif",
    variantId: null,
    locationSelected: false
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Gelato product mappings
  // ─────────────────────────────────────────────────────────────────────────

  var GELATO_UIDS = {
    'Poster': {
      '12" x 16"': 'flat_300x400-mm-12x16-inch_200-gsm-80lb-uncoated_4-0_ver',
      '18" x 24"': 'flat_450x600-mm-18x24-inch_200-gsm-80lb-uncoated_4-0_ver',
      '24" x 32"': 'flat_600x800-mm-24x32-inch_200-gsm-80lb-uncoated_4-0_ver'
    },
    'Wooden Framed Poster': {
      '12" x 16"': {
        'Black':       'framed_poster_mounted_300x400-mm-12x16-inch_black_wood_w12xt22-mm_plexiglass_300x400-mm-12x16-inch_200-gsm-80lb-uncoated_4-0_ver',
        'Chestnut':    'framed_poster_mounted_300x400-mm-12x16-inch_dark-wood_wood_w12xt22-mm_plexiglass_300x400-mm-12x16-inch_200-gsm-80lb-uncoated_4-0_ver',
        'Natural Oak': 'framed_poster_mounted_300x400-mm-12x16-inch_natural-wood_wood_w12xt22-mm_plexiglass_300x400-mm-12x16-inch_200-gsm-80lb-uncoated_4-0_ver',
        'White':       'framed_poster_mounted_300x400-mm-12x16-inch_white_wood_w12xt22-mm_plexiglass_300x400-mm-12x16-inch_200-gsm-80lb-uncoated_4-0_ver'
      },
      '18" x 24"': {
        'Black':       'framed_poster_mounted_450x600-mm-18x24-inch_black_wood_w12xt22-mm_plexiglass_450x600-mm-18x24-inch_200-gsm-80lb-uncoated_4-0_ver',
        'Chestnut':    'framed_poster_mounted_450x600-mm-18x24-inch_dark-wood_wood_w12xt22-mm_plexiglass_450x600-mm-18x24-inch_200-gsm-80lb-uncoated_4-0_ver',
        'Natural Oak': 'framed_poster_mounted_450x600-mm-18x24-inch_natural-wood_wood_w12xt22-mm_plexiglass_450x600-mm-18x24-inch_200-gsm-80lb-uncoated_4-0_ver',
        'White':       'framed_poster_mounted_450x600-mm-18x24-inch_white_wood_w12xt22-mm_plexiglass_450x600-mm-18x24-inch_200-gsm-80lb-uncoated_4-0_ver'
      },
      '24" x 32"': {
        'Black':       'framed_poster_mounted_600x800-mm-24x32-inch_black_wood_w12xt22-mm_plexiglass_600x800-mm-24x32-inch_200-gsm-80lb-uncoated_4-0_ver',
        'Chestnut':    'framed_poster_mounted_600x800-mm-24x32-inch_dark-wood_wood_w12xt22-mm_plexiglass_600x800-mm-24x32-inch_200-gsm-80lb-uncoated_4-0_ver',
        'Natural Oak': 'framed_poster_mounted_600x800-mm-24x32-inch_natural-wood_wood_w12xt22-mm_plexiglass_600x800-mm-24x32-inch_200-gsm-80lb-uncoated_4-0_ver',
        'White':       'framed_poster_mounted_600x800-mm-24x32-inch_white_wood_w12xt22-mm_plexiglass_600x800-mm-24x32-inch_200-gsm-80lb-uncoated_4-0_ver'
      }
    },
    'Metal Framed Poster': {
      '12" x 16"': { 'Black': 'framed_poster_mounted_300x400-mm-12x16-inch_black_aluminum_w10xt22-mm_plexiglass_300x400-mm-12x16-inch_200-gsm-80lb-uncoated_4-0_ver' },
      '18" x 24"': { 'Black': 'framed_poster_mounted_450x600-mm-18x24-inch_black_aluminum_w10xt22-mm_plexiglass_450x600-mm-18x24-inch_200-gsm-80lb-uncoated_4-0_ver' },
      '24" x 32"': { 'Black': 'framed_poster_mounted_600x800-mm-24x32-inch_black_aluminum_w10xt22-mm_plexiglass_600x800-mm-24x32-inch_200-gsm-80lb-uncoated_4-0_ver' }
    },
    'Canvas': {
      '12" x 16"': 'canvas_300x400-mm-12x16-inch_canvas_wood-fsc-slim_4-0_ver',
      '18" x 24"': 'canvas_450x600-mm-18x24-inch_canvas_wood-fsc-slim_4-0_ver',
      '24" x 32"': 'canvas_600x800-mm-24x32-inch_canvas_wood-fsc-slim_4-0_ver'
    },
    'Wooden Framed Canvas': {
      '12" x 16"': {
        'Black':       'framed_canvas_geo_simplified_300x400-mm-12x16-inch_black_352x452-mm-14x18-inch_wood-fsc-slim_ver_wood_w14xt42-mm_canvas_4-0',
        'Chestnut':    'framed_canvas_geo_simplified_300x400-mm-12x16-inch_dark-wood_352x452-mm-14x18-inch_wood-fsc-slim_ver_wood_w14xt42-mm_canvas_4-0',
        'Natural Oak': 'framed_canvas_geo_simplified_300x400-mm-12x16-inch_natural-wood_352x452-mm-14x18-inch_wood-fsc-slim_ver_wood_w14xt42-mm_canvas_4-0'
      },
      '18" x 24"': {
        'Black':       'framed_canvas_geo_simplified_450x600-mm-18x24-inch_black_502x652-mm-20x26-inch_wood-fsc-slim_ver_wood_w14xt42-mm_canvas_4-0',
        'Chestnut':    'framed_canvas_geo_simplified_450x600-mm-18x24-inch_dark-wood_502x652-mm-20x26-inch_wood-fsc-slim_ver_wood_w14xt42-mm_canvas_4-0',
        'Natural Oak': 'framed_canvas_geo_simplified_450x600-mm-18x24-inch_natural-wood_502x652-mm-20x26-inch_wood-fsc-slim_ver_wood_w14xt42-mm_canvas_4-0'
      },
      '24" x 32"': {
        'Black':       'framed_canvas_geo_simplified_600x800-mm-24x32-inch_black_652x852-mm-26x34-inch_wood-fsc-slim_ver_wood_w14xt42-mm_canvas_4-0',
        'Chestnut':    'framed_canvas_geo_simplified_600x800-mm-24x32-inch_dark-wood_652x852-mm-26x34-inch_wood-fsc-slim_ver_wood_w14xt42-mm_canvas_4-0',
        'Natural Oak': 'framed_canvas_geo_simplified_600x800-mm-24x32-inch_natural-wood_652x852-mm-26x34-inch_wood-fsc-slim_ver_wood_w14xt42-mm_canvas_4-0'
      }
    }
  };

  var SIZE_MAP = {
    '12" x 16"': { w: 3638, h: 4819, ratio: 1.325 },
    '18" x 24"': { w: 5410, h: 7182, ratio: 1.4 },
    '24" x 32"': { w: 7050, h: 9400, ratio: 1.5 }
  };

  var CANVAS_SIZE_MAP = {
    '12" x 16"': { w: 4701, h: 5882 },
    '18" x 24"': { w: 6473, h: 8245 },
    '24" x 32"': { w: 7050, h: 9400 }
  };

  var FRAME_COLOURS = {
    'Black':       '#1a1a1a',
    'White':       '#f5f5f5',
    'Natural Oak': '#b8924a',
    'Chestnut':    '#5c2e1a'
  };

  var MAP_WIDTHS = { '12" x 16"': '82%', '18" x 24"': '82%', '24" x 32"': '82%' };
  var DESKTOP_TEXT_TOPS = { '12" x 16"': '70%', '18" x 24"': '70%', '24" x 32"': '70%' };
  var MOBILE_TEXT_TOP = '72%';

  // ─────────────────────────────────────────────────────────────────────────
  // Utilities
  // ─────────────────────────────────────────────────────────────────────────

  function isMobile() {
    return window.innerWidth <= MOBILE_BREAKPOINT;
  }

  function needsFrame(format) {
    return format === 'Wooden Framed Poster' || format === 'Metal Framed Poster' || format === 'Wooden Framed Canvas';
  }

  function isCanvasFormat(format) {
    return format === 'Canvas' || format === 'Wooden Framed Canvas';
  }

  function $(id) {
    return document.getElementById(id);
  }

  function formatCoords(lat, lon) {
    var latDir = lat >= 0 ? 'N' : 'S';
    var lonDir = lon >= 0 ? 'E' : 'W';
    return Math.abs(lat).toFixed(2) + '° ' + latDir + ', ' + Math.abs(lon).toFixed(2) + '° ' + lonDir;
  }

  function formatDate(date) {
    var months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
    var d = date.getDate();
    var suffix = (d % 100 >= 11 && d % 100 <= 13) ? 'th' : d % 10 === 1 ? 'st' : d % 10 === 2 ? 'nd' : d % 10 === 3 ? 'rd' : 'th';
    return d + suffix + ' ' + months[date.getMonth()] + ' ' + date.getFullYear();
  }

  function formatTime(date) {
    var h = date.getHours();
    var m = date.getMinutes();
    var ampm = h >= 12 ? 'pm' : 'am';
    var displayHour = h % 12;
    if (displayHour === 0) displayHour = 12;
    return displayHour + ':' + (m < 10 ? '0' + m : m) + ' ' + ampm;
  }

  function getBrightness(hex) {
    var r = parseInt(hex.slice(1,3), 16);
    var g = parseInt(hex.slice(3,5), 16);
    var b = parseInt(hex.slice(5,7), 16);
    return (r * 299 + g * 587 + b * 114) / 1000;
  }

  function getGelatoUid() {
    var formatData = GELATO_UIDS[state.format];
    if (!formatData) return null;
    var sizeData = formatData[state.size];
    if (!sizeData) return null;
    if (typeof sizeData === 'string') return sizeData;
    return sizeData[state.frameColour] || null;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Auto-scale text to fit a given width (mobile only)
  // ─────────────────────────────────────────────────────────────────────────

  function fitTextToWidth(element, maxWidth, startSize, minSize) {
    if (!element || !element.textContent || !maxWidth) return;
    var size = startSize;
    element.style.fontSize = size + 'px';
    element.style.whiteSpace = 'nowrap';
    // Shrink until it fits or hits the floor
    while (element.scrollWidth > maxWidth && size > minSize) {
      size -= 0.5;
      element.style.fontSize = size + 'px';
    }
    // If still overflowing at the minimum, allow wrapping as last resort
    if (element.scrollWidth > maxWidth) {
      element.style.whiteSpace = 'normal';
    }
  }

  function applyMobileAutoScale() {
    if (!isMobile()) return;
    var mapWrapper = $('sm-map-wrapper');
    var nameEl = $('sm-poster-name');
    var msgEl = $('sm-poster-message');
    if (!mapWrapper) return;

    // Available width = map wrapper width minus small padding
    var maxWidth = mapWrapper.offsetWidth - 16;
    if (maxWidth <= 0) return;

    // Use the most-recently-set font size as the starting point
    if (nameEl) {
      var nameStart = parseFloat(nameEl.style.fontSize) || 20;
      fitTextToWidth(nameEl, maxWidth, nameStart, 10);
    }
    if (msgEl) {
      var msgStart = parseFloat(msgEl.style.fontSize) || 12;
      fitTextToWidth(msgEl, maxWidth, msgStart, 7);
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Poster text updates
  // ─────────────────────────────────────────────────────────────────────────

  function updatePosterText() {
    var nameEl = $('sm-poster-name');
    var msgEl = $('sm-poster-message');
    var dateEl = $('sm-poster-date');
    var locEl = $('sm-poster-location');
    var coordEl = $('sm-poster-coords');

    if (nameEl) {
      nameEl.textContent = state.name || 'Your Heading';
      nameEl.style.opacity = state.name ? '1' : '0.75';
    }
    if (msgEl) {
      msgEl.textContent = state.message || '';
      msgEl.style.opacity = state.message ? '1' : '0.75';
    }
    if (dateEl) dateEl.textContent = formatDate(state.date);
    if (locEl) locEl.textContent = state.locationName;
    if (coordEl) coordEl.textContent = state.coordinates;

    // After text changes, re-fit on mobile
    applyMobileAutoScale();

    // The visible poster text is drawn by the SVG layer — redraw it so typed
    // heading/subheading (and date/coords/location) update live. No-ops in the
    // DOM-fallback path (refreshChrome guards on window.PosterSVG + the layer).
    refreshChrome();
  }

  function updateFrameColour() {
    var poster = $('sm-poster');
    if (!poster) return;
    var colour = needsFrame(state.format) ? (FRAME_COLOURS[state.frameColour] || '#1a1a1a') : '#1a1a1a';
    poster.style.border = '12px solid ' + colour;
  }

  function updatePosterTheme(mode) {
    var poster = $('sm-poster');
    if (poster) poster.setAttribute('data-theme', mode);
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Layout & scaling (single source of truth)
  // ─────────────────────────────────────────────────────────────────────────

  function posterRatio() {
    // Mobile preview locked to an 18x24 shape so the GUI doesn't jump on size change.
    if (isMobile()) { var d = SIZE_MAP['18" x 24"']; return d.h / d.w; }
    // Desktop preview locked to 24x32 shape so the poster doesn't visibly resize
    // when the customer picks a different print size (only frame/format changes).
    var isC = isCanvasFormat(state.format);
    var lockedDims = (isC ? CANVAS_SIZE_MAP : SIZE_MAP)['24" x 32"'];
    return lockedDims.h / lockedDims.w;
  }

  // withSlot=true bakes the moon INTO the svg (for the enlarge, the confirm modal
  // and anything that needs a standalone image). The live editor leaves it out and
  // shows the moon as a DOM canvas instead, so it can transition smoothly on a size
  // change the way the star map's celestial canvas does.
  function buildPosterSVG(opts) {
    var bg = backgroundOpts();
    var o = {
      ratio: posterRatio(),
      border: state.showBorder,
      noRing: true,          // a moon has no ring around it
      glow: bg.glow,
      stars: bg.stars,
      details: moonDetails()
    };
    if (opts && opts.withSlot && moonHref) {
      o.slotImageHref = moonHref;
      o.slotNoZoom = true;   // the moon square is already the final crop
      o.slotNoClip = true;   // let its baked bloom spill past the slot circle
      o.slotScale = 1.0;
    }
    return PosterSVG.renderPosterSVG(state, o);
  }

  // Light refresh: redraw ONLY the SVG chrome (text, bg, border) in place.
  function refreshChrome() {
    if (!window.PosterSVG) return;
    var layer = $('sm-poster-svg-layer');
    if (!layer) return;
    layer.innerHTML = buildPosterSVG();
    var e = layer.querySelector('svg');
    if (e) {
      // Match renderEditorPoster so an in-place refresh can't flip aspect handling
      // (which would shift the border a hair).
      e.setAttribute('preserveAspectRatio', 'none');
      e.style.width = '100%'; e.style.height = '100%'; e.style.display = 'block';
    }
  }

  function renderEditorPoster() {
    var preview = document.querySelector('.sm-preview');
    var poster = $('sm-poster');
    if (!preview || !poster || !window.PosterSVG) return;

    // Animate only on desktop, and only when the poster shape (ratio) actually
    // changes — i.e. a real size switch. Location / colour / text refreshes keep the
    // same ratio, so they paint instantly with no transition (no border wobble).
    var ratio = posterRatio();
    var ratioChanged = (lastRenderedRatio !== null) && (Math.abs(lastRenderedRatio - ratio) > 0.0005);

    // Same approach as the star map: animate the box, let the SVG chrome stretch with
    // it, and transition the moon's own element alongside. That's why the moon is a
    // DOM canvas in the editor rather than being baked into the SVG.
    var animate = !isMobile() && ratioChanged;
    var boxTrans = animate
      ? ('width ' + SIZE_DUR + ' ' + SIZE_EASE + ', height ' + SIZE_DUR + ' ' + SIZE_EASE +
         ', transform ' + SIZE_DUR + ' ' + SIZE_EASE + ', margin-bottom ' + SIZE_DUR + ' ' + SIZE_EASE)
      : 'none';
    var slotTrans = animate
      ? ('left ' + SIZE_DUR + ' ' + SIZE_EASE + ', top ' + SIZE_DUR + ' ' + SIZE_EASE +
         ', width ' + SIZE_DUR + ' ' + SIZE_EASE + ', height ' + SIZE_DUR + ' ' + SIZE_EASE)
      : 'none';

    var Wpx = isMobile() ? Math.min(preview.offsetWidth - 32, MOBILE_POSTER_MAX_WIDTH) : 600;
    var Hpx = Math.round(Wpx * ratio);
    poster.style.transition = boxTrans;
    poster.style.background = state.bgColor;   // keep the box bg matched to the SVG bg
    poster.style.width = Wpx + 'px';
    poster.style.height = Hpx + 'px';
    poster.style.transform = 'none';
    poster.style.marginBottom = '0';
    if (!isMobile()) {
      var fit = Math.min((preview.offsetWidth - 48) / Wpx, (preview.offsetHeight - 48) / Hpx, 1);
      poster.style.transform = 'scale(' + fit + ')';
      poster.style.transformOrigin = 'top center';
      poster.style.marginBottom = ((Hpx * fit) - Hpx) + 'px';
    }

    var oldText = document.querySelector('.sm-poster-text'); if (oldText) oldText.style.display = 'none';
    var oldBorder = $('sm-poster-border'); if (oldBorder) oldBorder.style.display = 'none';

    var layer = $('sm-poster-svg-layer');
    if (!layer) {
      layer = document.createElement('div');
      layer.id = 'sm-poster-svg-layer';
      layer.style.cssText = 'position:absolute;inset:0;z-index:1;pointer-events:none;';
      poster.insertBefore(layer, poster.firstChild);
    }

    // Crossfade when the MOON itself changed (a new phase), so it dissolves rather
    // than snapping. Text and colour edits repaint instantly — fading those would
    // just feel laggy.
    var markup = buildPosterSVG();
    if (fadeNext && layer.innerHTML) {
      fadeNext = false;
      var ghost = document.createElement('div');
      ghost.style.cssText = 'position:absolute;inset:0;z-index:2;pointer-events:none;transition:opacity .4s ease;';
      ghost.innerHTML = layer.innerHTML;
      var gsvg = ghost.querySelector('svg');
      if (gsvg) { gsvg.setAttribute('preserveAspectRatio','xMidYMid meet'); gsvg.style.width='100%'; gsvg.style.height='100%'; gsvg.style.display='block'; }
      poster.insertBefore(ghost, layer.nextSibling);
      layer.innerHTML = markup;
      requestAnimationFrame(function(){
        requestAnimationFrame(function(){
          ghost.style.opacity = '0';
          setTimeout(function(){ if (ghost.parentNode) ghost.parentNode.removeChild(ghost); }, 450);
        });
      });
    } else {
      layer.innerHTML = markup;
    }

    var svgEl = layer.querySelector('svg');
    if (svgEl) {
      // 'none' couples the SVG to the box so the chrome stretches WITH it during a
      // size change rather than re-snapping. Safe now that the moon is a DOM canvas
      // and not part of this SVG — it's what kept the star map's transition smooth.
      svgEl.setAttribute('preserveAspectRatio', 'none');
      svgEl.style.width = '100%'; svgEl.style.height = '100%'; svgEl.style.display = 'block';
    }

    // MUST match what buildPosterSVG() passes, or the moon's wrapper gets placed
    // using one layout while the SVG's text is laid out with another — the details
    // strip shrinks and raises the slot, so omitting it here drops an oversized moon
    // straight through the heading.
    var L = PosterSVG.computeLayout({ ratio: ratio, details: !!moonDetails() });

    // Place the moon's wrapper into the slot circle, by % of the box so the poster's
    // border can't offset it. With slotTrans applied it eases between sizes.
    var mapWrapper = $('sm-map-wrapper');
    if (mapWrapper) {
      mapWrapper.style.display = '';
      mapWrapper.style.transition = slotTrans;
      mapWrapper.style.position = 'absolute';
      mapWrapper.style.margin = '0';
      mapWrapper.style.aspectRatio = 'auto';
      mapWrapper.style.left   = ((L.slot.cx - L.slot.r) / L.vbW * 100) + '%';
      mapWrapper.style.top    = ((L.slot.cy - L.slot.r) / L.vbH * 100) + '%';
      mapWrapper.style.width  = (L.slot.d / L.vbW * 100) + '%';
      mapWrapper.style.height = (L.slot.d / L.vbH * 100) + '%';
      mapWrapper.style.zIndex = '2';
    }

    lastRenderedRatio = ratio;
  }

  function updateMapScale() {
    if (window.PosterSVG) { return renderEditorPoster(); }
    var preview = document.querySelector('.sm-preview');
    var poster = $('sm-poster');
    if (!preview || !poster) return;

    var nameEl = $('sm-poster-name');
    var msgEl = $('sm-poster-message');
    var locEl = $('sm-poster-location');
    var coordEl = $('sm-poster-coords');
    var dateEl = $('sm-poster-date');
    var divEl = document.querySelector('.sm-poster-divider');
    var textEl = document.querySelector('.sm-poster-text');
    var mapWrapper = $('sm-map-wrapper');

    if (isMobile()) {
      // ── Mobile layout ──
      var availW = preview.offsetWidth - 32;
      var mobileW = Math.min(availW, MOBILE_POSTER_MAX_WIDTH);
      var mobileH = Math.round(mobileW * 1.3333);

      poster.style.width = mobileW + 'px';
      poster.style.height = mobileH + 'px';
      poster.style.transform = 'none';
      poster.style.marginBottom = '0';

      if (mapWrapper) mapWrapper.style.width = MAP_WIDTHS[state.size] || '78%';

      var fs = mobileW / 600;

      // Use the actual final mobile width as the reference so values stay deterministic
      var refMobileW = MOBILE_POSTER_MAX_WIDTH;
      var refFs = refMobileW / 600;
      var metaFontPx = Math.round(8 * refFs * 1.6);
      // Floor of 7px so the metadata never collapses to nothing
      if (metaFontPx < 7) metaFontPx = 7;

      if (nameEl) {
        nameEl.style.fontSize = Math.round(26 * fs * 1.6) + 'px';
        nameEl.style.lineHeight = '1.1';
      }
      if (msgEl) {
        msgEl.style.fontSize = Math.round(16 * fs * 1.6) + 'px';
        msgEl.style.lineHeight = '1.2';
      }
      if (locEl) locEl.style.fontSize = metaFontPx + 'px';
      if (coordEl) coordEl.style.fontSize = metaFontPx + 'px';
      if (dateEl) dateEl.style.fontSize = metaFontPx + 'px';
      if (divEl) divEl.style.width = Math.round(20 * fs) + 'px';
      if (textEl) {
        textEl.style.top = MOBILE_TEXT_TOP;
        textEl.style.bottom = 'auto';
      }

      // Apply auto-scale after sizes are set
      applyMobileAutoScale();
    } else {
      // ── Desktop layout ──
      var sizeData = SIZE_MAP[state.size];
      var newHeight = sizeData ? Math.round(600 * sizeData.ratio) : 700;
      var availWdesk = preview.offsetWidth - 48;
      var availH = preview.offsetHeight - 48;
      var scale = Math.min(availWdesk / 600, availH / newHeight, 1);

      poster.style.transition = 'height 0.4s ease, transform 0.4s ease, margin-bottom 0.4s ease, background 0.3s ease';
      poster.style.width = '600px';
      poster.style.height = newHeight + 'px';
      poster.style.transform = 'scale(' + scale + ')';
      poster.style.transformOrigin = 'top center';
      poster.style.marginBottom = ((newHeight * scale) - newHeight) + 'px';

      if (mapWrapper) mapWrapper.style.width = MAP_WIDTHS[state.size] || '78%';

      if (textEl) {
        textEl.style.transition = 'top 0.4s ease';
        textEl.style.top = DESKTOP_TEXT_TOPS[state.size] || '72%';
        textEl.style.bottom = 'auto';
      }

      // Desktop font sizes are constant (text doesn't scale with print size)
      var transStyle = 'font-size 0.4s ease';
      [nameEl, msgEl, locEl, coordEl, dateEl].forEach(function(el) {
        if (el) el.style.transition = transStyle;
      });

      if (nameEl) {
        nameEl.style.fontSize = '28px';
        nameEl.style.lineHeight = '';
        nameEl.style.whiteSpace = '';
      }
      if (msgEl) {
        msgEl.style.fontSize = '17px';
        msgEl.style.lineHeight = '';
        msgEl.style.whiteSpace = '';
      }
      if (locEl) locEl.style.fontSize = '10px';
      if (coordEl) coordEl.style.fontSize = '10px';
      if (dateEl) dateEl.style.fontSize = '10px';
      if (divEl) divEl.style.width = '32px';
    }

  }

  function updateStep4UI() {
    var format = state.format;
    var size = state.size;
    var frameField = $('sm-frame-colour-field');

    // Show/hide frame colour field
    if (frameField) frameField.style.display = needsFrame(format) ? 'block' : 'none';

    // Filter frame colour swatches based on format
    var isMetalFrame = format === 'Metal Framed Poster';
    var isWoodenCanvas = format === 'Wooden Framed Canvas';
    document.querySelectorAll('.sm-swatch-option[data-frame-colour]').forEach(function(sw) {
      var colour = sw.dataset.frameColour;
      if (isMetalFrame && colour !== 'Black') {
        sw.style.display = 'none';
      } else if (isWoodenCanvas && colour === 'White') {
        sw.style.display = 'none';
      } else {
        sw.style.display = '';
      }
    });

    // Reset to Black if the current frame colour is invalid for the new format
    if ((isMetalFrame && state.frameColour !== 'Black') || (isWoodenCanvas && state.frameColour === 'White')) {
      state.frameColour = 'Black';
      document.querySelectorAll('.sm-swatch-option[data-frame-colour]').forEach(function(s) {
        s.classList.remove('sm-active');
      });
      var blackSwatch = document.querySelector('.sm-swatch-option[data-frame-colour="Black"]');
      if (blackSwatch) blackSwatch.classList.add('sm-active');
    }

    // Recompute layout for the new size
    updateMapScale();

    // Update Gelato UID, variant and price
    state.gelatoUid = getGelatoUid();
    updateVariantFromState();
    updatePriceDisplay();
    updateFrameColour();
  }

  function updateVariantFromState() {
    var variants = window.MOON_VARIANTS || [];
    var variant = variants.find(function(v) {
      return v.option1 === state.size && v.option2 === state.format;
    });
    if (variant) {
      state.variantId = variant.id;
      var variantInput = $('sm-variant-id');
      if (variantInput) variantInput.value = variant.id;
    }
  }

  function updatePriceDisplay() {
    var variants = window.MOON_VARIANTS || [];
    var variant = variants.find(function(v) {
      return v.option1 === state.size && v.option2 === state.format;
    });
    var priceEl = $('sm-price-display');
    if (variant && priceEl) {
      var format = window.MOON_MONEY_FORMAT || '£{{amount}}';
      var amount = (variant.price / 100).toFixed(2);
      priceEl.textContent = format
        .replace('{{amount}}', amount)
        .replace('{{amount_no_decimals}}', Math.round(variant.price / 100))
        .replace('{{amount_with_comma_separator}}', amount);
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Location autocomplete
  // ─────────────────────────────────────────────────────────────────────────

  function initLocationAutocomplete() {
    var input = $('sm-location');
    if (!input) return;

    var dropdown = document.createElement('div');
    dropdown.style.cssText = 'position:absolute;z-index:99999;background:#fff;border:1px solid #ddd;border-top:none;max-height:250px;overflow-y:auto;box-shadow:0 4px 12px rgba(0,0,0,0.15);border-radius:0 0 8px 8px;display:none;';
    document.body.appendChild(dropdown);

    function positionDropdown() {
      var rect = input.getBoundingClientRect();
      var scrollY = window.pageYOffset || document.documentElement.scrollTop;
      dropdown.style.position = 'absolute';
      dropdown.style.top = (rect.bottom + scrollY) + 'px';
      dropdown.style.left = rect.left + 'px';
      dropdown.style.width = rect.width + 'px';
    }

    var debounceTimer;
    var currentReqId = 0;
    input.addEventListener('input', function() {
      clearTimeout(debounceTimer);
      var query = input.value.trim();
      if (query.length < 3) {
        dropdown.style.display = 'none';
        return;
      }
      // Show a searching state immediately so the customer gets feedback while
      // the geocoder responds.
      dropdown.innerHTML = '<div style="padding:12px 14px;font-size:13px;color:#8a8a8a;font-family:Montserrat,sans-serif;display:flex;align-items:center;gap:10px;"><div style="width:14px;height:14px;border:1.5px solid #e5e1db;border-top-color:#C9A96E;border-radius:50%;animation:sm-spin 0.8s linear infinite;"></div><span>Searching…</span></div>';
      positionDropdown();
      dropdown.style.display = 'block';
      debounceTimer = setTimeout(function() {
        var reqId = ++currentReqId;
        // Mapbox Geocoding v6 — fast, reliable, 100k free/month on standard tier.
        // Falls back to photon.komoot.io if the token isn't configured.
        var mapboxToken = (window.MOON_CONFIG || {}).mapbox_token;
        var geocodeUrl = mapboxToken
          ? 'https://api.mapbox.com/search/geocode/v6/forward?q=' + encodeURIComponent(query) + '&limit=5&language=en&access_token=' + encodeURIComponent(mapboxToken)
          : 'https://photon.komoot.io/api/?q=' + encodeURIComponent(query) + '&limit=5&lang=en';
        fetch(geocodeUrl)
          .then(function(r) { return r.json(); })
          .then(function(data) {
            // Ignore stale responses (customer typed more since we fired this).
            if (reqId !== currentReqId) return;
            dropdown.innerHTML = '';
            if (!data.features || !data.features.length) {
              dropdown.style.display = 'none';
              return;
            }
            data.features.forEach(function(feature) {
              var props = feature.properties || {};
              var coords = feature.geometry.coordinates;
              // Mapbox exposes place_formatted / full_address; photon uses name/city/country.
              var displayName;
              if (props.full_address || props.place_formatted) {
                displayName = props.full_address || props.place_formatted;
              } else {
                var parts = [props.name || ''];
                if (props.city && props.city !== props.name) parts.push(props.city);
                if (props.country && props.country !== props.name) parts.push(props.country);
                displayName = parts.join(', ');
              }
              var item = document.createElement('div');
              item.style.cssText = 'padding:10px 14px;cursor:pointer;font-size:14px;border-bottom:1px solid #f0f0f0;font-family:Montserrat,sans-serif;color:#0A1628;';
              item.textContent = displayName;
              item.addEventListener('mouseenter', function() { this.style.background = '#f5f0eb'; });
              item.addEventListener('mouseleave', function() { this.style.background = '#fff'; });
              item.addEventListener('mousedown', function(e) {
                e.preventDefault();
                input.value = displayName;
                state.lat = coords[1];
                state.lon = coords[0];
                state.locationName = displayName;
                state.locationSelected = true;
                state.coordinates = formatCoords(coords[1], coords[0]);
                dropdown.style.display = 'none';
                updatePosterText();
                // The phase is the same everywhere on Earth, but the moon's TILT
                // isn't — so a new location means a new render.
                refreshMoon();
              });
              dropdown.appendChild(item);
            });
            positionDropdown();
            dropdown.style.display = 'block';
          })
          .catch(function() { dropdown.style.display = 'none'; });
      }, 300);
    });

    input.addEventListener('blur', function() {
      setTimeout(function() { dropdown.style.display = 'none'; }, 200);
    });

    input.addEventListener('keydown', function(e) {
      if (e.key === 'Escape') dropdown.style.display = 'none';
    });
  }

  // ─────────────────────────────────────────────────────────────────────────
  // ─────────────────────────────────────────────────────────────────────────

  // ── The moon ────────────────────────────────────────────────────────────────
  // Rendered off-screen into a square, then handed to the poster engine as an
  // image. Nothing lives in the DOM slot, which is why this is so much lighter
  // than the star map's celestial handling.
  var MOON_PX   = 1100;   // editor resolution; the print re-renders at print size
  var moonHref  = '';     // current moon square as a data URL
  var moonInfo  = null;   // { phase, fraction, age, frame, rotation, name }
  var moonSeq   = 0;      // guards against out-of-order frame loads
  var fadeNext  = false;  // crossfade the next repaint (set when the moon changes)

  function moonReady() { return !!moonHref; }

  // The poster starts at opacity 0 (class sm-poster-loading in starmap.css) and the
  // star map clears it once celestial has drawn. There's no celestial here, so the
  // moon has to do it — otherwise everything renders perfectly and stays invisible.
  //
  // Fades in once (like the star map) instead of popping. Uses the Web Animations
  // API rather than a CSS transition, because renderEditorPoster() sets an inline
  // transition of 'none' on the poster, which would cut a CSS fade short.
  var posterRevealed = false;
  function revealPoster() {
    var poster = $('sm-poster');
    if (poster) {
      var wasHidden = poster.classList.contains('sm-poster-loading');
      poster.classList.remove('sm-poster-loading');
      poster.classList.add('sm-poster-ready');
      if (wasHidden && !posterRevealed && poster.animate) {
        poster.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 800, easing: 'ease' });
      }
      posterRevealed = true;
    }
    var loader = $('sm-map-loading');
    if (loader) loader.style.display = 'none';
  }

  // Paint a new moon into the live canvas. When a moon is already showing, the old
  // one is copied onto a ghost canvas laid exactly over the live one, then faded
  // out, so a new date or place dissolves into the new moon rather than snapping.
  // A quick run of changes just restarts the fade from whatever is on screen.
  var MOON_FADE_MS = 700;
  function paintMoonCanvas(cv, src, crossfade) {
    var parent = cv.parentNode;
    var oldGhost = document.getElementById('sm-moon-ghost');
    if (oldGhost && oldGhost.parentNode) oldGhost.parentNode.removeChild(oldGhost);

    var ghost = null;
    if (crossfade && parent && cv.width && cv.height && cv.animate) {
      ghost = document.createElement('canvas');
      ghost.id = 'sm-moon-ghost';
      ghost.width = cv.width;
      ghost.height = cv.height;
      ghost.getContext('2d').drawImage(cv, 0, 0);
      ghost.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;object-fit:contain;pointer-events:none;display:block;';
      parent.insertBefore(ghost, cv.nextSibling);   // sits on top of the live canvas
    }

    cv.width = src.width;
    cv.height = src.height;
    cv.getContext('2d').drawImage(src, 0, 0);

    if (ghost) {
      var anim = ghost.animate([{ opacity: 1 }, { opacity: 0 }], { duration: MOON_FADE_MS, easing: 'ease-in-out', fill: 'forwards' });
      anim.onfinish = function () { if (ghost.parentNode) ghost.parentNode.removeChild(ghost); };
    }
  }

  // Re-render the moon for the current date/place, then repaint the poster.
  // Frames load at different speeds, so a run of quick date changes can resolve
  // out of order — only the newest request is allowed to paint.
  function refreshMoon() {
    if (!window.MoonEngine) return Promise.resolve();
    if (isNaN(state.date.getTime())) return Promise.resolve();

    var seq = ++moonSeq;
    var prevFrame = moonInfo ? moonInfo.frame : null;
    var loading = $('sm-map-loading');
    if (loading && !moonHref) loading.style.display = '';

    // Phase, illumination and age are pure maths — instant, no network. Update them
    // NOW so the details strip never shows a stale figure and then jumps when the
    // image lands. Only the picture has to wait.
    moonInfo = MoonEngine.compute(state.date, state.lat, state.lon);
    if (moonHref) renderEditorPoster();

    return MoonEngine.renderSquare(state, MOON_PX).then(function (res) {
      if (seq !== moonSeq) return;
      // Paint the live editor's canvas...
      var cv = $('sm-moon-canvas');
      if (cv) paintMoonCanvas(cv, res.canvas, !!moonHref);   // crossfade if a moon was already showing
      // ...and keep a data URL for the enlarge / confirm / print.
      try { moonHref = res.canvas.toDataURL('image/png'); }
      catch (e) { moonHref = ''; }   // tainted => R2 CORS is wrong
      var changed = (prevFrame !== null && prevFrame !== res.info.frame);
      moonInfo = res.info;
      if (loading) loading.style.display = 'none';
      revealPoster();
      fadeNext = changed;
      renderEditorPoster();
    }).catch(function () {
      if (seq !== moonSeq) return;
      if (loading) loading.style.display = 'none';
      revealPoster();   // show the poster even if the moon failed, rather than a blank panel
      renderEditorPoster();
    });
  }

  // The phase strip under the caption, when switched on.
  function moonDetails() {
    if (!state.showDetails || !moonInfo) return null;
    return [
      { label: 'Phase',        value: moonInfo.name },
      { label: 'Illumination', value: Math.round(moonInfo.fraction * 100) + '%' },
      { label: 'Moon age',     value: moonInfo.age.toFixed(1) + ' days' }
    ];
  }

  // What the engine draws behind the moon. Glow and stars are independent, and the
  // glow is derived from the background so it complements it instead of being a
  // fixed blue that fights a purple or black poster.
  function glowColour() {
    var hex = (state.bgColor || '#131c2e').replace('#', '');
    if (hex.length === 3) hex = hex[0]+hex[0]+hex[1]+hex[1]+hex[2]+hex[2];
    var r = parseInt(hex.substr(0,2),16), g = parseInt(hex.substr(2,2),16), b = parseInt(hex.substr(4,2),16);
    if (isNaN(r)) { r = 10; g = 10; b = 10; }
    // Lift the background well towards white, keeping its hue.
    function lift(v){ return Math.round(v + (255 - v) * 0.72); }
    return 'rgb(' + lift(r) + ',' + lift(g) + ',' + lift(b) + ')';
  }

  function backgroundOpts() {
    return {
      stars: state.showStars ? { count: 380, seed: 20260715, colour: state.starColor || '#ffffff' } : null,
      glow:  state.showGlow  ? { colour: glowColour(), radius: 2.8, alpha: 0.18 } : null
    };
  }

  var WORKER_URL = 'https://starmap-worker.thestarsabove.workers.dev';

  // Serialise the design state into something the Worker/render page can use.
  // Excludes Date object (rehydrated on the other end from ISO), variantId
  // and other UI-only fields — just what draw() reads.
  function serialiseRenderState() {
    return {
      bgColor: state.bgColor,
      starColor: state.starColor,
      showStars: state.showStars,
      showGlow: state.showGlow,
      showBorder: state.showBorder,
      showDetails: state.showDetails,
      name: state.name,
      message: state.message,
      font: state.font,
      subheadingFont: state.subheadingFont,
      locationName: state.locationName,
      coordinates: state.coordinates,
      date: state.date && state.date.toISOString ? state.date.toISOString() : new Date(state.date).toISOString(),
      lat: state.lat,
      lon: state.lon,
      size: state.size,
      format: state.format,
      frameColour: state.frameColour,
      rotate: state.rotate
    };
  }

  // R2 public URL prefix. Files land at /starmaps/{orderId}.jpg. Hardcoded so
  // the customer's browser doesn't need to round-trip to learn where the file
  // will be — that's what makes the confirm click feel instant.
  var R2_PUBLIC_URL = 'https://pub-cb76d8ae32574abe97650946e8421f75.r2.dev';

  // Fire-and-forget render. Kicks off the server render on Cloudflare Browser
  // Rendering, then resolves IMMEDIATELY with the URL where the file will land
  // once the render completes. The customer proceeds to cart / checkout with
  // no visible wait; the render finishes in the background.
  //
  // Safety net: if the initial POST to the Worker doesn't return 200, the
  // client-side fallback renders in the browser so no order goes to Gelato
  // with a missing file. In practice this shouldn't fire — the Worker uses
  // ctx.waitUntil() to accept the job and return 200 within ~100ms, well
  // before Browser Rendering has done any real work.
  function uploadToWorker(orderId) {
    return new Promise(function (resolve, reject) {
      if (!window.PosterSVG) { reject(new Error('Poster engine not loaded')); return; }

      var predictedUrl = R2_PUBLIC_URL + '/starmaps/' + orderId + '.jpg';

      // Kick off the render. We don't await this — we resolve below with the
      // predicted URL as soon as the POST is in flight.
      fetch(WORKER_URL + '/render-print/moon', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ state: serialiseRenderState(), orderId: orderId })
      }).then(function (r) {
        if (!r.ok) {
          console.warn('Server render kick-off HTTP ' + r.status + ' — running client fallback');
          uploadToWorkerClient(orderId);
        }
        // If 200: server render is in progress on Cloudflare, nothing more to do here.
      }).catch(function (err) {
        console.warn('Server render kick-off failed:', err && err.message, '— running client fallback');
        uploadToWorkerClient(orderId);
      });

      // Resolve immediately so the customer can proceed. The file will be at
      // predictedUrl by the time the 4-hour cron submits the order to Gelato.
      resolve(predictedUrl);
    });
  }

  // Pre-2026 client-side render — kept as fallback so a Cloudflare outage
  // doesn't take the store down. Remove once server render has proven itself
  // over a couple of hundred orders.
  function uploadToWorkerClient(orderId) {
    var workerUrl = WORKER_URL;

    return new Promise(function(resolve, reject) {
      if (!window.PosterSVG) { reject(new Error('Poster engine not loaded')); return; }

      var isCanvas = isCanvasFormat(state.format);
      var sizeData = isCanvas ? CANVAS_SIZE_MAP[state.size] : SIZE_MAP[state.size];
      var W = sizeData ? sizeData.w : 4701;
      var H = sizeData ? sizeData.h : 6473;
      var ratio = H / W;

      // Canvas formats: keep the design inside the printable safe zone; the background
      // wraps out to the physical frame edge.
      var safeInset = null;
      var canvasSafeZones = {
        '12" x 16"': { w: 3554, h: 4709 },
        '18" x 24"': { w: 5320, h: 7094 },
        '24" x 32"': { w: 7085, h: 9450 }
      };
      if (isCanvas && canvasSafeZones[state.size]) {
        safeInset = PosterSVG.safeInsetFrac({ w: W, h: H }, canvasSafeZones[state.size]);
      }

      // Layout from the SAME engine the editor and enlarge modal use — single source of
      // truth, so the print can't drift from what the customer designed.
      // noSub: with no subheading the heading drops into its space (matches the engine).
      var L = PosterSVG.computeLayout({ ratio: ratio, safeInset: safeInset, details: !!moonDetails(), noSub: !String(state.message || '').trim() });
      var scale = W / L.vbW;                 // viewBox units → print pixels
      function S(v){ return v * scale; }

      // The moon is rendered fresh at PRINT resolution below (not upscaled from the
      // editor's 1100px square), so 24x32 stays sharp. Capped so a huge print doesn't
      // ask the browser for an unreasonable canvas.
      var moonPx = Math.min(Math.round(S(L.slot.d)), 3072);

      // Styling mirrored from render-poster-svg.js (keep in sync with the engine).
      var GRAD_TOP = 16, GRAD_BOT = -14;
      function shade(hex, d){
        hex = (hex || '#0a0a0a').replace('#', ''); if (hex.length === 3) hex = hex[0]+hex[0]+hex[1]+hex[1]+hex[2]+hex[2];
        var r = parseInt(hex.substr(0,2),16), g = parseInt(hex.substr(2,2),16), b = parseInt(hex.substr(4,2),16);
        if (isNaN(r)) { r = 10; g = 10; b = 10; }
        function cl(v){ return Math.max(0, Math.min(255, Math.round(v))); }
        function hx(v){ return ('0' + cl(v).toString(16)).slice(-2); }
        return '#' + hx(r+d) + hx(g+d) + hx(b+d);
      }
      function isLightBg(hex){
        if (!hex) return false; hex = hex.replace('#',''); if (hex.length === 3) hex = hex[0]+hex[0]+hex[1]+hex[1]+hex[2]+hex[2];
        var r = parseInt(hex.substr(0,2),16), g = parseInt(hex.substr(2,2),16), b = parseInt(hex.substr(4,2),16);
        if (isNaN(r)) return false; return (0.2126*r + 0.7152*g + 0.0722*b) > 150;
      }
      function hexA(hex, a){
        hex = (hex || '#ffffff').replace('#','');
        if (hex.length === 3) hex = hex[0]+hex[0]+hex[1]+hex[1]+hex[2]+hex[2];
        var r = parseInt(hex.substr(0,2),16), g = parseInt(hex.substr(2,2),16), b = parseInt(hex.substr(4,2),16);
        if (isNaN(r)) { r=255; g=255; b=255; }
        return 'rgba(' + r + ',' + g + ',' + b + ',' + a + ')';
      }
      var light = isLightBg(state.bgColor);
      var col = {
        head: light ? 'rgba(15,25,45,0.95)' : 'rgba(255,255,255,0.95)',
        sub:  light ? 'rgba(15,25,45,0.82)' : 'rgba(255,255,255,0.82)',
        div:  light ? 'rgba(15,25,45,0.55)' : 'rgba(255,255,255,0.55)',
        cap:  light ? 'rgba(15,25,45,0.72)' : 'rgba(255,255,255,0.72)',
        bord: light ? 'rgba(15,25,45,0.45)' : 'rgba(255,255,255,0.55)'
      };
      var bg = state.bgColor || '#0a0a0a';

      function draw(mapSrc){
        var canvas = document.createElement('canvas');
        canvas.width = W; canvas.height = H;
        var ctx = canvas.getContext('2d');

        // Background — gradient (matches engine); flat if both deltas are 0
        if (GRAD_TOP === 0 && GRAD_BOT === 0) {
          ctx.fillStyle = bg;
        } else {
          var grad = ctx.createLinearGradient(0, 0, 0, H);
          grad.addColorStop(0, shade(bg, GRAD_TOP));
          grad.addColorStop(0.45, bg);
          grad.addColorStop(1, shade(bg, GRAD_BOT));
          ctx.fillStyle = grad;
        }
        ctx.fillRect(0, 0, W, H);

        var sl = L.slot;
        var bgo = backgroundOpts();

        // Atmospheric glow — mirrors the engine's radial gradient (gaussian falloff,
        // so there's no visible circular edge).
        if (bgo.glow && bgo.glow.alpha > 0) {
          var gR = S(sl.r) * bgo.glow.radius;
          var rg = ctx.createRadialGradient(S(sl.cx), S(sl.cy), 0, S(sl.cx), S(sl.cy), gR);
          for (var gi = 0; gi <= 12; gi++) {
            var gt = gi / 12;
            rg.addColorStop(gt, hexA(bgo.glow.colour, bgo.glow.alpha * Math.exp(-4.2 * gt * gt)));
          }
          ctx.fillStyle = rg;
          ctx.beginPath(); ctx.arc(S(sl.cx), S(sl.cy), gR, 0, Math.PI * 2); ctx.fill();
        }

        // Starfield — same seed as the preview, so the print matches exactly.
        if (bgo.stars && bgo.stars.count > 0) {
          var sd = bgo.stars.seed >>> 0;
          var rnd = function () {                    // mulberry32
            sd |= 0; sd = (sd + 0x6D2B79F5) | 0;
            var t = Math.imul(sd ^ (sd >>> 15), 1 | sd);
            t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
            return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
          };
          for (var si = 0; si < bgo.stars.count; si++) {
            var sx = rnd() * W, sy = rnd() * H, mag = rnd();
            var sr = S(0.5 + mag * mag * 2.2), so = 0.25 + rnd() * 0.6;
            ctx.globalAlpha = so;
            ctx.fillStyle = bgo.stars.colour;
            ctx.beginPath(); ctx.arc(sx, sy, sr, 0, Math.PI * 2); ctx.fill();
          }
          ctx.globalAlpha = 1;
        }

        // The moon. No clip — its baked bloom is meant to spill past the disc.
        if (mapSrc) {
          ctx.drawImage(mapSrc, S(sl.cx - sl.d/2), S(sl.cy - sl.d/2), S(sl.d), S(sl.d));
        }

        // Keyline border
        if (state.showBorder) {
          ctx.strokeStyle = col.bord;
          ctx.lineWidth = S(L.border.width);
          ctx.strokeRect(
            S(L.content.x + L.border.inset), S(L.content.y + L.border.inset),
            S(L.content.w - 2 * L.border.inset), S(L.content.h - 2 * L.border.inset)
          );
        }

        // Text — canvas fillText uses the browser's loaded fonts, so it matches the
        // editor exactly. Baseline is alphabetic to mirror SVG <text y=…>.
        var t = L.text;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'alphabetic';

        ctx.fillStyle = col.head;
        ctx.font = '300 ' + S(t.heading.size) + 'px ' + (state.font || "'Cormorant Garamond', serif");
        ctx.fillText(state.name || '', S(t.heading.x), S(t.heading.y));

        ctx.fillStyle = col.sub;
        ctx.font = 'italic 300 ' + S(t.sub.size) + 'px ' + (state.subheadingFont || "'Montserrat', sans-serif");
        ctx.fillText(state.message || '', S(t.sub.x), S(t.sub.y));

        ctx.strokeStyle = col.div;
        ctx.lineWidth = Math.max(1, S(1));
        ctx.beginPath();
        ctx.moveTo(S(t.divider.x1), S(t.divider.y));
        ctx.lineTo(S(t.divider.x2), S(t.divider.y));
        ctx.stroke();

        ctx.fillStyle = col.cap;
        ctx.font = '500 ' + S(t.caption.size) + 'px "Montserrat", sans-serif';
        var hadLS = ('letterSpacing' in ctx);
        if (hadLS) { try { ctx.letterSpacing = S(0.6) + 'px'; } catch (e) {} }
        var loc = (state.locationName || '').toUpperCase();
        var coord = state.coordinates || '';
        var dateStr = formatDate(state.date).toUpperCase();
        if (loc)     ctx.fillText(loc,     S(t.caption.x), S(t.caption.lines[0]));
        if (coord)   ctx.fillText(coord,   S(t.caption.x), S(t.caption.lines[1]));
        if (dateStr) ctx.fillText(dateStr, S(t.caption.x), S(t.caption.lines[2]));
        if (hadLS) { try { ctx.letterSpacing = '0px'; } catch (e) {} }

        // Phase details strip, if switched on
        var det = moonDetails();
        if (det && L.details) {
          var D = L.details, span = D.colHalf * 2, step = span / det.length;
          var dx0 = D.cx - span/2 + step/2;
          ctx.textAlign = 'center'; ctx.textBaseline = 'alphabetic';
          for (var di = 0; di < det.length; di++) {
            var dcx = dx0 + di*step;
            ctx.fillStyle = col.cap;
            ctx.font = '500 ' + S(D.labelSize) + 'px "Montserrat", sans-serif';
            ctx.globalAlpha = 0.75;
            ctx.fillText(String(det[di].label).toUpperCase(), S(dcx), S(D.labelY));
            ctx.globalAlpha = 1;
            ctx.fillStyle = col.head;
            ctx.font = '600 ' + S(D.valueSize) + 'px "Montserrat", sans-serif';
            ctx.fillText(String(det[di].value).toUpperCase(), S(dcx), S(D.valueY));
            if (di > 0) {
              ctx.strokeStyle = col.div; ctx.globalAlpha = 0.5;
              ctx.lineWidth = Math.max(1, S(0.8));
              ctx.beginPath();
              ctx.moveTo(S(dcx - step/2), S(D.labelY - 9));
              ctx.lineTo(S(dcx - step/2), S(D.valueY + 4));
              ctx.stroke(); ctx.globalAlpha = 1;
            }
          }
        }

        // Export + upload — pipeline unchanged (R2 -> 4h queue -> cron -> Gelato).
        // JPEG, not PNG: a 24x32 PNG is ~199MB and ~265MB once base64'd for the POST,
        // which is most of the wait. Measured on a poster slice (dark bg + 400 stars +
        // the moon's hot limb): q95 gives 50 dB PSNR, where ~45 dB is the visually-
        // lossless threshold. The worst error is faint ringing at star edges, which at
        // 300dpi is sub-millimetre. q95 over q92 costs ~36% size for extra headroom —
        // still ~6x smaller than PNG, and you can't cheaply re-print a bad order.
        canvas.toBlob(function(blob){
          var reader = new FileReader();
          reader.onload = function(){
            fetch(workerUrl + '/upload', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ orderId: orderId, png: reader.result })
            })
            .then(function(res){ return res.json(); })
            .then(function(data){ if (!data.url) throw new Error('Upload failed'); resolve(data.url); })
            .catch(reject);
          };
          reader.readAsDataURL(blob);
        }, 'image/jpeg', 0.95);
      }

      function proceed(mapSrc){
        if (document.fonts && document.fonts.ready) {
          document.fonts.ready.then(function(){ draw(mapSrc); }, function(){ draw(mapSrc); });
        } else {
          draw(mapSrc);
        }
      }

      // Render the moon fresh at print resolution, then compose. Falls back to the
      // editor's square if that fails, so an order never gets stuck.
      MoonEngine.renderSquare(state, moonPx).then(function (res) {
        proceed(res.canvas);
      }).catch(function () {
        if (!moonHref) { proceed(null); return; }
        var im = new Image();
        im.onload = function () { proceed(im); };
        im.onerror = function () { proceed(null); };
        im.src = moonHref;
      });
    });
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Preview snapshot (used in confirm modal and preview modal)
  // ─────────────────────────────────────────────────────────────────────────

  // Build the enlarge/confirm image from the SVG engine — the same renderer the
  // editor and the print use, so the three can't drift apart.
  function buildSvgSnapshot(reserveH) {
    if (!window.PosterSVG) return null;

    // Use the editor's own builder rather than re-listing the options here. Building
    // them twice is how the enlarge silently lost the glow, stars and phase details —
    // and it's the same drift that plagued the star map's three render paths.
    var svgMarkup = buildPosterSVG({ withSlot: true });
    if (!svgMarkup) return null;

    // Fit the poster within the viewport while preserving its aspect ratio.
    var ratio = posterRatio();                       // height / width
    var pad = needsFrame(state.format) ? 16 : 0;
    // reserveH = vertical space the modal needs for its own chrome (title, summary,
    // buttons). Sizing the poster to what's actually free means it never overflows
    // into the buttons, and never needs CSS to squash it (which caused letterboxing).
    var reserve = (typeof reserveH === 'number') ? reserveH : 120;
       var maxW = Math.min(window.innerWidth - 48, 440) - pad * 2;
    var maxH = (window.innerHeight - reserve) - pad * 2;
    if (maxW < 40) maxW = 40;
    if (maxH < 40) maxH = 40;
    var drawW = maxW, drawH = maxW * ratio;
    if (drawH > maxH) { drawH = maxH; drawW = maxH / ratio; }

    var wrapper = document.createElement('div');
    wrapper.className = 'sm-preview-snapshot-wrap';
    var frameBg = needsFrame(state.format) ? (FRAME_COLOURS[state.frameColour] || '#1a1a1a') : 'transparent';
    var frameShadow = needsFrame(state.format) ? '0 20px 60px rgba(0,0,0,0.6)' : '0 10px 40px rgba(0,0,0,0.4)';
    wrapper.style.cssText = 'background:' + frameBg + ';padding:' + pad + 'px;border-radius:0;display:inline-block;line-height:0;box-shadow:' + frameShadow + ';';

    var holder = document.createElement('div');
    holder.className = 'sm-preview-snapshot';
    // Set with priority: theme CSS was stretching this box wider than the poster's
    // aspect ratio, which made the SVG letterbox and show the modal backdrop as
    // bands either side. Exact px + !important keeps the box on-ratio.
    holder.style.setProperty('width', Math.round(drawW) + 'px', 'important');
    holder.style.setProperty('height', Math.round(drawH) + 'px', 'important');
    holder.style.setProperty('background', 'transparent', 'important');
    holder.style.setProperty('line-height', '0', 'important');
    holder.innerHTML = svgMarkup;

    var svgEl = holder.querySelector('svg');
    if (svgEl) {
      svgEl.setAttribute('preserveAspectRatio', 'xMidYMid meet');
      svgEl.style.setProperty('display', 'block', 'important');
      svgEl.style.setProperty('width', Math.round(drawW) + 'px', 'important');
      svgEl.style.setProperty('height', Math.round(drawH) + 'px', 'important');
    }

    wrapper.appendChild(holder);
    return wrapper;
  }

  function generatePreviewSnapshot(reserveH) {
    // Preferred: render from the SVG engine so the modal matches the editor exactly.
    // Falls back to the DOM-measurement path below only if PosterSVG isn't present.
    var svgSnap = window.PosterSVG ? buildSvgSnapshot(reserveH) : null;
    if (svgSnap) return svgSnap;

    var poster = $('sm-poster');
    if (!poster) return null;

    // ── Measure the LIVE poster as the single source of truth ──
    // Every dimension below is READ from what's actually rendered, then scaled by K.
    // That's why the enlarge matches the live view exactly and can't drift.
    var posterRect = poster.getBoundingClientRect();
    if (!posterRect.width || !posterRect.height) return null;

    // desktop applies transform:scale() to the poster; neutralise it so font sizes
    // (which are in CSS px, pre-transform) land in the same space as the rects.
    var posterScale = posterRect.width / (poster.offsetWidth || posterRect.width);

    var OUTPUT_W = 1200;                         // ~native map res (1000px source) + crisp text
    var K = OUTPUT_W / posterRect.width;
    var W = Math.round(posterRect.width * K);
    var H = Math.round(posterRect.height * K);

    var snapCanvas = document.createElement('canvas');
    snapCanvas.width = W;
    snapCanvas.height = H;
    var ctx = snapCanvas.getContext('2d');

    function relRect(el) {
      var r = el.getBoundingClientRect();
      return {
        x:  (r.left - posterRect.left) * K,
        y:  (r.top  - posterRect.top)  * K,
        w:  r.width  * K,
        h:  r.height * K,
        cx: (r.left - posterRect.left + r.width  / 2) * K,
        cy: (r.top  - posterRect.top  + r.height / 2) * K
      };
    }

    // ── Background ──
    ctx.fillStyle = state.bgColor;
    ctx.fillRect(0, 0, W, H);

    // (No moon here: this DOM-measuring path only runs if PosterSVG failed to load,
    // in which case buildSvgSnapshot — which draws the moon properly — is unavailable
    // anyway. Kept only as a last-resort text/border snapshot.)

    // ── Border (if the live poster shows one) ──
    var borderEl = $('sm-poster-border');
    if (borderEl && getComputedStyle(borderEl).display !== 'none') {
      var b = relRect(borderEl);
      var bcs = getComputedStyle(borderEl);
      var bcol = bcs.borderTopColor;
      ctx.strokeStyle = (bcol && bcol !== 'rgba(0, 0, 0, 0)') ? bcol : 'rgba(255,255,255,0.6)';
      ctx.lineWidth = Math.max(1, (parseFloat(bcs.borderTopWidth) || 1) * posterScale * K);
      ctx.strokeRect(b.x, b.y, b.w, b.h);
    }

    // ── Text: each line drawn from its measured rect, font, colour & opacity ──
    function drawTextEl(el) {
      if (!el) return;
      var txt = (el.textContent || '').trim();
      if (!txt) return;
      var cs = getComputedStyle(el);
      // honour CSS text-transform (live uses uppercase on location/coords/date)
      var tt = cs.textTransform;
      if (tt === 'uppercase') txt = txt.toUpperCase();
      else if (tt === 'lowercase') txt = txt.toLowerCase();
      else if (tt === 'capitalize') txt = txt.replace(/\b\w/g, function(m){ return m.toUpperCase(); });
      var r = relRect(el);
      var fontPx = parseFloat(cs.fontSize) * posterScale * K;
      ctx.font = cs.fontStyle + ' ' + cs.fontWeight + ' ' + fontPx + 'px ' + cs.fontFamily;
      ctx.fillStyle = cs.color;
      ctx.globalAlpha = parseFloat(cs.opacity || '1');
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(txt, r.cx, r.cy);
      ctx.globalAlpha = 1;
    }

    drawTextEl($('sm-poster-name'));
    drawTextEl($('sm-poster-message'));

    // ── Divider ──
    var divEl = document.querySelector('.sm-poster-divider');
    if (divEl && getComputedStyle(divEl).display !== 'none') {
      var d = relRect(divEl);
      var dcol = getComputedStyle(divEl).backgroundColor;
      if (!dcol || dcol === 'rgba(0, 0, 0, 0)') dcol = 'rgba(255,255,255,0.5)';
      ctx.fillStyle = dcol;
      ctx.fillRect(d.x, d.cy - Math.max(1, d.h / 2), d.w, Math.max(1, d.h || K));
    }

    drawTextEl($('sm-poster-location'));
    drawTextEl($('sm-poster-coords'));
    drawTextEl($('sm-poster-date'));

    // No watermark. The preview renders at ~900px where a 12x16 print needs 3638 and
    // a 24x32 needs 7050 — a screenshot prints badly on its own. A mark across the
    // artwork costs every genuine viewer at the exact moment they're deciding whether
    // it's beautiful enough to buy, to deter a few people who'd get a poor result
    // anyway. Several competitors don't watermark either.

    // ── Wrapper + frame styling (unchanged) ──
    var wrapper = document.createElement('div');
    wrapper.className = 'sm-preview-snapshot-wrap';
    var framePadding = needsFrame(state.format) ? 16 : 0;
    var frameBg = needsFrame(state.format) ? (FRAME_COLOURS[state.frameColour] || '#1a1a1a') : 'transparent';
    var frameShadow = needsFrame(state.format) ? '0 20px 60px rgba(0,0,0,0.6)' : '0 10px 40px rgba(0,0,0,0.4)';
    wrapper.style.cssText = 'background:' + frameBg + ';padding:' + framePadding + 'px;border-radius:0;display:inline-block;box-shadow:' + frameShadow + ';';

    var img = document.createElement('img');
    img.src = snapCanvas.toDataURL('image/png');
    img.className = 'sm-preview-snapshot';
    img.style.cssText = 'display:block;max-width:100%;max-height:calc(100vh - 120px);width:auto;height:auto;';
    wrapper.appendChild(img);
    return wrapper;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Add to cart flow
  // ─────────────────────────────────────────────────────────────────────────

  var addingToCart = false;

  function addToCart() {
    // Guard against double-tap / confirm button firing alongside add button.
    if (addingToCart) return;
    addingToCart = true;

    var btn = $('sm-add-btn');
    if (btn) btn.disabled = true;

    // Release the guard on any exit path (success shows modal, error alerts).
    // No overlay theatre — the render is fire-and-forget on Cloudflare, so
    // there's no client work to wait through.
    var cleanupOverlay = function() {
      addingToCart = false;
    };

    var orderId = 'sm-' + Date.now();
    uploadToWorker(orderId)
      .then(function(printUrl) {
        var variantId = state.variantId || ($('sm-variant-id') && $('sm-variant-id').value);
        if (!variantId) throw new Error('No variant selected');

        function pad(n) { return n < 10 ? '0' + n : '' + n; }
        var isoTimestamp = state.date.getFullYear() + '-' +
                           pad(state.date.getMonth() + 1) + '-' +
                           pad(state.date.getDate()) + 'T' +
                           pad(state.date.getHours()) + ':' +
                           pad(state.date.getMinutes());
        var preciseCoords = state.lat.toFixed(4) + ',' + state.lon.toFixed(4);

        var properties = {
          '_print_url': printUrl,
          '_gelato_uid': state.gelatoUid || '',
          '_order_id': orderId,
          '_iso_timestamp': isoTimestamp,
          '_precise_coords': preciseCoords,
          // Full design state for server-side print rendering. Underscore-prefixed keys
          // are hidden from the customer's cart but still visible in Shopify admin.
          '_bg_color': state.bgColor,
          '_star_color': state.starColor,
          '_glow': state.showGlow ? '1' : '0',
          '_stars': state.showStars ? '1' : '0',
          '_show_details': state.showDetails ? '1' : '0',
          '_moon_frame': moonInfo ? String(moonInfo.frame) : '',
          '_moon_phase': moonInfo ? moonInfo.name : '',
          '_moon_illumination': moonInfo ? Math.round(moonInfo.fraction * 100) + '%' : '',
          '_moon_age': moonInfo ? moonInfo.age.toFixed(1) : '',
          '_heading_font': state.font,
          '_subheading_font': state.subheadingFont,
          '_show_border': state.showBorder ? '1' : '0',
          'Location': state.locationName,
          'Date': formatDate(state.date),
          'Heading': state.name,
          'Subheading': state.message,
          'Size': state.size,
          'Format': state.format
        };
        if (needsFrame(state.format)) properties['Frame Color'] = state.frameColour;

        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 1, properties: properties })
        });
      })
      .then(function(res) {
        if (!res.ok) throw new Error('Failed to add to cart');
        cleanupOverlay();

        // Second-item flow: if the customer is here from the modal offer
        // (flag set), they've now designed their second item — go straight to
        // cart and clear the flag. Otherwise this is a first item; show the
        // modal offering a second design at 25% off before routing to cart.
        var isSecondItem = false;
        try {
          isSecondItem = sessionStorage.getItem('tsa_second_item_flow') === '1';
          if (isSecondItem) sessionStorage.removeItem('tsa_second_item_flow');
        } catch (e) {}

        var cartRedirect = (window.MOON_CONFIG && window.MOON_CONFIG.cart_redirect) || '/cart';

        if (isSecondItem) {
          // Overlay stays visible ("Adding to your cart…") until the page
          // navigates — the browser's own transition then takes over.
          window.location.href = cartRedirect + '?updated=' + Date.now();
        } else {
          if (btn) btn.disabled = false;
          transitionPostBuyToOffer(cartRedirect);
        }
      })
      .catch(function(err) {
        console.error(err);
        cleanupOverlay();
        hidePostBuyOverlay();
        if (btn) btn.disabled = false;
        alert('Error: ' + err.message);
      });
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Initial date/time population
  // ─────────────────────────────────────────────────────────────────────────

  var MOON_TIME = '22:00';

  function populateDateSelects() {
    var now = new Date();
    var dateEl = $('sm-date');
    var timeEl = $('sm-time');

    if (dateEl) {
      var yyyy = now.getFullYear();
      var mm = String(now.getMonth() + 1).padStart(2, '0');
      var dd = String(now.getDate()).padStart(2, '0');
      dateEl.value = yyyy + '-' + mm + '-' + dd;
      dateEl.min = '1900-01-01';
    }
    // The moon has no time field: every moon is calculated for 10:00 PM local, the
    // same default the product page uses. The hidden input keeps updateDate() and
    // saved designs working unchanged.
    if (timeEl) timeEl.value = MOON_TIME;
    state.date = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 22, 0);
  }

  function initShippingMessage() {
    var el = $('sm-shipping-msg');
    if (el) el.textContent = 'Complimentary Shipping Included';
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Input validation (Next button on step 1 and 2)
  // ─────────────────────────────────────────────────────────────────────────

  function flashFieldError(input, errorPlaceholder, originalPlaceholder) {
    input.style.borderColor = '#c0392b';
    input.style.boxShadow = '0 0 0 3px rgba(192,57,43,0.15)';
    input.placeholder = errorPlaceholder;
    setTimeout(function() {
      input.style.borderColor = '';
      input.style.boxShadow = '';
      input.placeholder = originalPlaceholder;
    }, 3000);
  }

  function wireStepValidation() {
    var step1NextBtn = $('sm-next-step1');
    if (step1NextBtn) {
      step1NextBtn.addEventListener('click', function(e) {
        var input = $('sm-location');
        if (!input || !input.value.trim() || !state.locationSelected) {
          e.stopImmediatePropagation();
          flashFieldError(input, 'Please enter a location first...', 'Search for a city or place...');
          input.focus();
        }
      }, true);
    }

    var step2NextBtn = $('sm-next-step2');
    if (step2NextBtn) {
      step2NextBtn.addEventListener('click', function(e) {
        // Only the heading is required. The subheading is optional, and the
        // poster simply leaves that line out when it's blank.
        var headingInput = $('sm-name-input');
        var headingEmpty = !headingInput || !headingInput.value.trim();
        if (headingEmpty) {
          e.stopImmediatePropagation();
          flashFieldError(headingInput, 'Please enter a heading...', 'Enter a heading or name...');
          headingInput.focus();
        }
      }, true);
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Modal helpers (confirm + preview)
  // ─────────────────────────────────────────────────────────────────────────

  function wireModals() {
    // Final confirmation modal
    var confirmModal = $('sm-confirm-modal');
    var confirmPreview = $('sm-confirm-preview');
    var confirmSummary = $('sm-confirm-summary');
    var confirmEditBtn = $('sm-confirm-edit');
    var confirmBuyBtn = $('sm-confirm-buy');

    function showConfirmModal() {
      if (!confirmModal || !confirmPreview || !confirmSummary) return;
      // Leave room for the heading, the size/price summary and both buttons.
      var snapshot = generatePreviewSnapshot(360);
      if (!snapshot) return;

      confirmPreview.innerHTML = '';
      confirmPreview.appendChild(snapshot);

      // Hide live poster to reduce iOS memory pressure during pinch-zoom
      var livePoster = $('sm-poster');
      if (livePoster) livePoster.style.visibility = 'hidden';

      // Summary line
      var variants = window.MOON_VARIANTS || [];
      var variant = variants.find(function(v) {
        return v.option1 === state.size && v.option2 === state.format;
      });
      var priceStr = '';
      if (variant) {
        var fmt = window.MOON_MONEY_FORMAT || '£{{amount}}';
        var amount = (variant.price / 100).toFixed(2);
        priceStr = fmt
          .replace('{{amount}}', amount)
          .replace('{{amount_no_decimals}}', Math.round(variant.price / 100))
          .replace('{{amount_with_comma_separator}}', amount);
      }
      var formatLabel = needsFrame(state.format) ? (state.frameColour + ' ' + state.format) : state.format;
      confirmSummary.innerHTML = '<strong>' + state.size + '</strong> &nbsp;·&nbsp; <strong>' + formatLabel + '</strong>' +
        (priceStr ? '<br><strong style="font-size:14px;letter-spacing:0.08em;">' + priceStr + '</strong>' : '');

      // The SVG snapshot hugs the poster's exact aspect ratio, so any background on
      // the container shows as bands beside it. Keep the container invisible.
      confirmPreview.style.background = 'transparent';
      confirmPreview.style.boxShadow = 'none';

      confirmModal.classList.add('sm-visible');
      document.body.style.overflow = 'hidden';
    }

    function hideConfirmModal(skipRerender) {
      if (!confirmModal) return;
      confirmModal.classList.remove('sm-visible');
      document.body.style.overflow = '';
      var livePoster = $('sm-poster');
      if (livePoster) livePoster.style.visibility = '';
      setTimeout(function() {
        if (confirmPreview) confirmPreview.innerHTML = '';
        // Skipped when going to checkout: rebuilding the editor there re-scales the
        // map with a transition, which visibly jumps behind the cart overlay.
        if (!skipRerender) updateMapScale();
      }, 300);
    }

    var addBtn = $('sm-add-btn');
    if (addBtn) addBtn.addEventListener('click', showConfirmModal);
    if (confirmEditBtn) confirmEditBtn.addEventListener('click', function() { hideConfirmModal(); });
    if (confirmBuyBtn) {
      confirmBuyBtn.addEventListener('click', function() {
        // Show the post-buy overlay FIRST (covers the entire screen with the
        // same dark-blue palette as the confirm modal), then hide the confirm
        // modal beneath it. This prevents the editor flashing through during
        // the transition from confirm → offer / cart.
        showPostBuyOverlay();
        hideConfirmModal(true);
        addToCart();
      });
    }
    if (confirmModal) {
      confirmModal.addEventListener('click', function(e) {
        if (e.target === confirmModal) hideConfirmModal();
      });
    }

    // Preview enlarge modal
    var previewBtn = $('sm-preview-btn');
    var previewModal = $('sm-preview-modal');
    var previewModalInner = $('sm-preview-modal-inner');
    var previewModalClose = $('sm-preview-modal-close');

    function openPreviewModal(e) {
      if (e) { e.preventDefault(); e.stopPropagation(); }
      if (!previewModal || !previewModalInner) return;
      var snapshot = generatePreviewSnapshot();
      if (!snapshot) return;
      previewModalInner.innerHTML = '';
      previewModalInner.appendChild(snapshot);
      var livePoster = $('sm-poster');
      if (livePoster) livePoster.style.visibility = 'hidden';
      previewModal.classList.add('sm-visible');
      document.body.style.overflow = 'hidden';
    }

    function closePreviewModal(e) {
      if (e) { e.preventDefault(); e.stopPropagation(); }
      if (!previewModal) return;
      previewModal.classList.remove('sm-visible');
      document.body.style.overflow = '';
      var livePoster = $('sm-poster');
      if (livePoster) livePoster.style.visibility = '';
      setTimeout(function() {
        if (previewModalInner) previewModalInner.innerHTML = '';
        updateMapScale();
      }, 300);
    }

    if (previewBtn) previewBtn.addEventListener('click', openPreviewModal);
    if (previewModalClose) previewModalClose.addEventListener('click', closePreviewModal);

    // On mobile, make the whole poster tappable to open the preview
    if (isMobile()) {
      var posterEl = $('sm-poster');
      if (posterEl) {
        posterEl.addEventListener('click', function(e) {
          // Don't fire if the user tapped the explicit button (it has its own handler)
          if (e.target.closest('#sm-preview-btn')) return;
          openPreviewModal(e);
        });
        posterEl.style.cursor = 'pointer';
      }
    }
    if (previewModal) {
      previewModal.addEventListener('click', function(e) {
        if (e.target === previewModal) closePreviewModal();
      });
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Wire up all the controls
  // ─────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────
  // Font choices only appear once there's text to style. Keeps step 2 tidy:
  // the heading font row shows once a heading is typed, the subheading font row
  // once a subheading is typed, and each hides again if its field is cleared.
  // The chosen font is kept either way.
  // ─────────────────────────────────────────────────────────────────────────

  function syncFontFields() {
    [['sm-name-input', 'sm-font-field-heading'], ['sm-message-input', 'sm-font-field-sub']].forEach(function (p) {
      var input = $(p[0]), field = $(p[1]);
      if (!input || !field) return;
      field.classList.toggle('is-collapsed', !input.value.trim());
    });
  }

  // Mark the font buttons that match the fonts in state (used after a saved
  // design is restored, so the highlighted button matches the poster).
  function syncFontButtons() {
    document.querySelectorAll('.sm-font-btn').forEach(function (b) {
      var target = b.dataset.fontTarget || 'message';
      var current = target === 'message' ? state.font : state.subheadingFont;
      b.classList.toggle('sm-active', b.dataset.font === current);
    });
  }

  function wireControls() {
    wireStepValidation();
    initLocationAutocomplete();

    // Reveal each font row once its field has text (and on load, in case the
    // browser restored typed values).
    ['sm-name-input', 'sm-message-input'].forEach(function (id) {
      var el = $(id);
      if (el) el.addEventListener('input', syncFontFields);
    });
    syncFontFields();

    // Mark text inputs / textareas as user-edited once typed in
    document.querySelectorAll('.sm-field input[type="text"], .sm-field textarea').forEach(function(el) {
      el.addEventListener('input', function() {
        if (el.value.trim()) el.classList.add('sm-user-edited');
        else el.classList.remove('sm-user-edited');
      });
    });

    // Date & time
    var dateEl = $('sm-date');
    var timeEl = $('sm-time');

    function updateDate() {
      var dateVal = (dateEl && dateEl.value) || '';
      var timeVal = (timeEl && timeEl.value) || '00:00';
      if (!dateVal) return;
      var dp = dateVal.split('-');
      var tp = timeVal.split(':');
      state.date = new Date(
        parseInt(dp[0], 10) || 2024,
        (parseInt(dp[1], 10) || 1) - 1,
        parseInt(dp[2], 10) || 1,
        parseInt(tp[0], 10) || 0,
        parseInt(tp[1], 10) || 0
      );
      updatePosterText();
      refreshMoon();   // new moment -> new phase, new frame, new tilt
    }

    [dateEl, timeEl].forEach(function(el) {
      if (el) {
        el.addEventListener('change', function() {
          el.classList.add('sm-user-edited');
          updateDate();
        });
      }
    });

    // Year validation on blur — allows typing (e.g. 2026 mid-type doesn't get clamped),
    // but snaps invalid or out-of-range years back on field exit. Mobile uses native
    // date picker so this only fires on desktop.
    if (dateEl) {
      dateEl.addEventListener('blur', function() {
        if (!dateEl.value) return;
        var dp = dateEl.value.split('-');
        var year = parseInt(dp[0], 10);
        var month = parseInt(dp[1], 10) || 1;
        var day = parseInt(dp[2], 10) || 1;
        var CURRENT_YEAR = new Date().getFullYear();
        var validYear = year;
        if (isNaN(year) || year < 1900) validYear = CURRENT_YEAR;
        if (year > 2100) validYear = CURRENT_YEAR;
        if (validYear !== year) {
          dateEl.value = validYear + '-' + (month < 10 ? '0' + month : month) + '-' + (day < 10 ? '0' + day : day);
          updateDate();
        }
      });
    }

    // Heading & subheading inputs
    var nameInput = $('sm-name-input');
    if (nameInput) {
      nameInput.addEventListener('input', function() {
        state.name = nameInput.value;
        updatePosterText();
      });
    }
    var msgInput = $('sm-message-input');
    if (msgInput) {
      msgInput.addEventListener('input', function() {
        state.message = msgInput.value;
        updatePosterText();
      });
    }

    // Character counters
    document.querySelectorAll('.sm-char-count').forEach(function(counter) {
      var input = $(counter.dataset.target);
      if (!input) return;
      var max = parseInt(input.getAttribute('maxlength'), 10) || 0;
      function updateCount() {
        var len = input.value.length;
        counter.textContent = len + ' / ' + max;
        counter.classList.remove('sm-near-limit', 'sm-at-limit');
        if (len >= max) counter.classList.add('sm-at-limit');
        else if (len >= max * 0.85) counter.classList.add('sm-near-limit');
      }
      input.addEventListener('input', updateCount);
      updateCount();
    });

    // Font selectors — heading affects only the heading, subheading affects only the subheading
    document.querySelectorAll('.sm-font-btn').forEach(function(btn) {
      btn.addEventListener('click', function() {
        var target = btn.dataset.fontTarget || 'message';
        document.querySelectorAll('.sm-font-btn[data-font-target="' + target + '"]').forEach(function(b) {
          b.classList.remove('sm-active');
        });
        btn.classList.add('sm-active');
        var font = btn.dataset.font;

        if (target === 'message') {
          state.font = font;
          var nameEl = $('sm-poster-name');
          if (nameEl) nameEl.style.fontFamily = font;
        } else {
          state.subheadingFont = font;
          var msgEl = $('sm-poster-message');
          if (msgEl) msgEl.style.fontFamily = font;
        }
        // Font change can change rendered width — re-fit on mobile
        applyMobileAutoScale();
        refreshChrome();
      });
    });

    // Style buttons (border)
    document.querySelectorAll('.sm-style-btn').forEach(function(btn) {
      btn.addEventListener('click', function() {
        document.querySelectorAll('.sm-style-btn').forEach(function(b) { b.classList.remove('sm-active'); });
        btn.classList.add('sm-active');
        state.showBorder = (btn.dataset.style === 'border');
        refreshChrome();
      });
    });

    // Background colour swatches
    document.querySelectorAll('.sm-swatch-option[data-bg]').forEach(function(sw) {
      sw.addEventListener('click', function() {
        if (sw.dataset.bg === 'custom') {
          var wrap = $('sm-bg-picker-wrap');
          if (wrap) wrap.style.display = wrap.style.display === 'none' ? 'flex' : 'none';
          return;
        }
        document.querySelectorAll('.sm-swatch-option[data-bg]').forEach(function(s) { s.classList.remove('sm-active'); });
        sw.classList.add('sm-active');
        state.bgColor = sw.dataset.bg;
        var poster = $('sm-poster');
        if (poster) poster.style.background = state.bgColor;
        updatePosterTheme('dark');
        renderEditorPoster();
      });
    });

    var bgCustomInput = $('sm-bg-custom');
    if (bgCustomInput) {
      bgCustomInput.addEventListener('change', function() {
        var val = this.value;
        document.querySelectorAll('.sm-swatch-option[data-bg]').forEach(function(s) { s.classList.remove('sm-active'); });
        var customSwatch = $('sm-bg-swatch-custom');
        if (customSwatch) customSwatch.classList.add('sm-active');
        state.bgColor = val;
        var poster = $('sm-poster');
        if (poster) poster.style.background = val;
        updatePosterTheme(getBrightness(val) > 160 ? 'light' : 'dark');
        renderEditorPoster();
      });
    }

    // Star colour swatches — on a moon poster this colours the starfield.
    document.querySelectorAll('.sm-swatch-option[data-star-color]').forEach(function(sw) {
      sw.addEventListener('click', function() {
        if (sw.dataset.starColor === 'custom') {
          var wrap = $('sm-star-picker-wrap');
          if (wrap) wrap.style.display = wrap.style.display === 'none' ? 'flex' : 'none';
          return;
        }
        document.querySelectorAll('.sm-swatch-option[data-star-color]').forEach(function(s) { s.classList.remove('sm-active'); });
        sw.classList.add('sm-active');
        state.starColor = sw.dataset.starColor;
        renderEditorPoster();
      });
    });

    var starCustomInput = $('sm-star-custom');
    if (starCustomInput) {
      starCustomInput.addEventListener('change', function() {
        var val = this.value;
        document.querySelectorAll('.sm-swatch-option[data-star-color]').forEach(function(s) { s.classList.remove('sm-active'); });
        var customSwatch = $('sm-star-swatch-custom');
        if (customSwatch) customSwatch.classList.add('sm-active');
        state.starColor = val;
        renderEditorPoster();
      });
    }

    // Element toggles (phase details, border)
    document.querySelectorAll('.sm-toggle-btn, .sm-sky-btn').forEach(function(btn) {
      if (!btn.dataset.toggle) return;   // the background-style buttons are handled below
      btn.addEventListener('click', function() {
        btn.classList.toggle('sm-active');
        var toggle = btn.dataset.toggle;
        var isOn = btn.classList.contains('sm-active');
        if (toggle === 'details') state.showDetails = isOn;
        if (toggle === 'border')  state.showBorder = isOn;
        if (toggle === 'glow')    state.showGlow = isOn;
        if (toggle === 'stars')   state.showStars = isOn;
        renderEditorPoster();
      });
    });

    // Step 4: size buttons
    document.querySelectorAll('.sm-size-btn').forEach(function(btn) {
      btn.addEventListener('click', function() {
        if (btn.disabled) return;
        document.querySelectorAll('.sm-size-btn').forEach(function(b) { b.classList.remove('sm-active'); });
        btn.classList.add('sm-active');
        state.size = btn.dataset.size;
        var sd = SIZE_MAP[state.size];
        if (sd) {
          state.printWidth = sd.w;
          state.printHeight = sd.h;
        }
        updateStep4UI();
      });
    });

    // Step 4: format buttons
    document.querySelectorAll('.sm-format-btn').forEach(function(btn) {
      btn.addEventListener('click', function() {
        document.querySelectorAll('.sm-format-btn').forEach(function(b) { b.classList.remove('sm-active'); });
        btn.classList.add('sm-active');
        state.format = btn.dataset.format;
        updateStep4UI();
      });
    });

    // Step 4: frame colour swatches
    document.querySelectorAll('.sm-swatch-option[data-frame-colour]').forEach(function(sw) {
      sw.addEventListener('click', function() {
        document.querySelectorAll('.sm-swatch-option[data-frame-colour]').forEach(function(s) { s.classList.remove('sm-active'); });
        sw.classList.add('sm-active');
        state.frameColour = sw.dataset.frameColour;
        state.gelatoUid = getGelatoUid();
        updateFrameColour();
      });
    });

    // Modals
    wireModals();

    // Status placeholder beneath cart button
    var cartSection = document.querySelector('.sm-cart-section');
    if (cartSection && !document.querySelector('.sm-status')) {
      var status = document.createElement('p');
      status.className = 'sm-status';
      status.style.cssText = 'text-align:center;font-size:12px;color:#C9A96E;margin-top:8px;min-height:20px;';
      cartSection.appendChild(status);
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Mobile: pinch-zoom prevention & snap-back after input/keyboard
  // ─────────────────────────────────────────────────────────────────────────

  function initMobileHandlers() {
    if (!isMobile()) return;

    // Prevent pinch-zoom on the customiser (causes iOS memory crashes)
    // but allow it inside the preview/confirm modals (static images only)
    document.addEventListener('touchmove', function(e) {
      var previewModal = $('sm-preview-modal');
      var confirmModal = $('sm-confirm-modal');
      if (previewModal && previewModal.classList.contains('sm-visible')) return;
      if (confirmModal && confirmModal.classList.contains('sm-visible')) return;
      if (e.touches.length > 1) e.preventDefault();
    }, { passive: false });

    var lastTouchEnd = 0;
    document.addEventListener('touchend', function(e) {
      var now = Date.now();
      if (now - lastTouchEnd <= 300) e.preventDefault();
      lastTouchEnd = now;
    }, false);

    // Snap layout back after iOS expands the page for inputs/pickers
    var snapTimer = null;
    var inputActive = false;

    function snapBack() {
      if (inputActive) return;
      var controls = document.querySelector('.sm-controls');
      if (!controls) return;
      controls.style.height = '';
      controls.style.minHeight = '';
      var maxScroll = document.body.scrollHeight - window.innerHeight;
      if (window.scrollY > maxScroll) {
        window.scrollTo(0, Math.max(0, maxScroll));
      }
    }

    document.addEventListener('focusin', function(e) {
      if (e.target.closest('.sm-controls')) inputActive = true;
    });

    document.addEventListener('focusout', function(e) {
      if (e.target.closest('.sm-controls')) {
        inputActive = false;
        clearTimeout(snapTimer);
        snapTimer = setTimeout(snapBack, 500);
      }
    });
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Init
  // ─────────────────────────────────────────────────────────────────────────

  // Make the sky toggle buttons reflect the current state defaults on load, so a
  // button hardcoded "on" in the Liquid can't disagree with the actual map (e.g.
  // Milky Way / constellation names now default off).
  function syncSkyToggles() {
    document.querySelectorAll('.sm-toggle-btn, .sm-sky-btn').forEach(function(btn) {
      var t = btn.dataset.toggle;
      var on;
      if (t === 'details')     on = state.showDetails;
      else if (t === 'border') on = state.showBorder;
      else if (t === 'glow')   on = state.showGlow;
      else if (t === 'stars')  on = state.showStars;
      else return;
      btn.classList.toggle('sm-active', !!on);
    });
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Product page prefill
  //
  // The product page box sends people here with their place, date (and, for the
  // star map, an optional time) in the link. These are applied to the state BEFORE
  // the first render, so the first moon the customer sees is theirs, not the
  // default one changing into theirs. Then it opens step 2 with a "Your moment"
  // summary. Saved-design links (?resume=) are left alone.
  // ─────────────────────────────────────────────────────────────────────────

  var PREFILL_DEFAULT_TIME = '22:00';

  function readUrlPrefill() {
    var params;
    try { params = new URLSearchParams(window.location.search); } catch (e) { return null; }
    if (params.get('resume')) return null;

    var place = params.get('tsa_place');
    var lat = parseFloat(params.get('tsa_lat'));
    var lon = parseFloat(params.get('tsa_lon'));
    var date = params.get('tsa_date');
    var time = params.get('tsa_time');
    var hasPlace = !!place && !isNaN(lat) && !isNaN(lon) && Math.abs(lat) <= 90 && Math.abs(lon) <= 180;
    var hasDate = /^\d{4}-\d{2}-\d{2}$/.test(date || '');
    var hasTime = false && /^\d{2}:\d{2}$/.test(time || '');
    if (!hasPlace && !hasDate) return null;

    return {
      place: place, lat: lat, lon: lon, hasPlace: hasPlace,
      date: date, hasDate: hasDate,
      time: hasTime ? time : PREFILL_DEFAULT_TIME, hasTime: hasTime
    };
  }

  // Runs inside init(), right after the defaults are set and before anything is drawn.
  function applyUrlPrefill(p) {
    if (!p) return;
    if (p.hasPlace) {
      state.lat = p.lat;
      state.lon = p.lon;
      state.locationName = p.place;
      state.coordinates = formatCoords(p.lat, p.lon);
      state.locationSelected = true;   // satisfies the step 1 check
      var li = $('sm-location');
      if (li) { li.value = p.place; li.classList.add('sm-user-edited'); }
    }

    var dateEl = $('sm-date');
    var timeEl = $('sm-time');
    var ymd = p.hasDate ? p.date : (dateEl && dateEl.value);
    if (p.hasDate && dateEl) { dateEl.value = p.date; dateEl.classList.add('sm-user-edited'); }
    if (timeEl) timeEl.value = p.time;

    if (ymd) {
      var dp = ymd.split('-'), tp = p.time.split(':');
      state.date = new Date(
        parseInt(dp[0], 10), parseInt(dp[1], 10) - 1, parseInt(dp[2], 10),
        parseInt(tp[0], 10) || 0, parseInt(tp[1], 10) || 0
      );
    }
  }

  // Runs once the controls exist: open step 2 and show the "Your moment" summary.
  function showPrefillStep(p) {
    if (!p || !p.hasPlace) return;
    jumpToStep(2);

    var step2 = $('sm-step-2');
    if (step2 && !document.getElementById('tsa-moment-summary')) {
      var dateTxt = state.date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
      var line = p.place + ' · ' + dateTxt;
      if (false) {
        line += ' · ' + state.date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) +
                (p.hasTime ? '' : ' (default)');
      }

      var box = document.createElement('div');
      box.id = 'tsa-moment-summary';
      box.style.cssText = 'display:flex;align-items:center;justify-content:space-between;gap:12px;padding:12px 14px;margin-bottom:16px;border:1px solid #e5e1db;border-radius:8px;background:#faf8f5;font-family:Montserrat,sans-serif;';

      var left = document.createElement('div');
      var label = document.createElement('div');
      label.textContent = 'Your moment';
      label.style.cssText = 'font-size:9.5px;font-weight:600;letter-spacing:0.12em;text-transform:uppercase;color:#C9A96E;margin-bottom:4px;';
      var txt = document.createElement('div');
      txt.textContent = line;
      txt.style.cssText = 'font-size:13px;line-height:1.4;color:#0A1628;';
      left.appendChild(label);
      left.appendChild(txt);

      var edit = document.createElement('button');
      edit.type = 'button';
      edit.textContent = 'Edit';
      edit.style.cssText = 'flex-shrink:0;background:none;border:0;padding:0;font-family:Montserrat,sans-serif;font-size:11px;font-weight:500;letter-spacing:0.08em;text-transform:uppercase;text-decoration:underline;color:#0A1628;cursor:pointer;';
      edit.addEventListener('click', function () { jumpToStep(1); });

      box.appendChild(left);
      box.appendChild(edit);
      var host = step2.querySelector('.sm-section') || step2;
      host.insertBefore(box, host.firstChild);
    }

    // Tidy the address bar so a refresh doesn't re-apply the link.
    try { history.replaceState(null, '', window.location.pathname); } catch (e) {}
  }

  function init() {
    var prefill = readUrlPrefill();
    populateDateSelects();
    applyUrlPrefill(prefill);   // before the first render
    updatePosterText();
    updateVariantFromState();

    // Initial poster dimensions
    var poster = $('sm-poster');
    if (poster) {
      if (isMobile()) {
        var initMobileW = Math.min(window.innerWidth - 32, MOBILE_POSTER_MAX_WIDTH);
        poster.style.width = initMobileW + 'px';
        poster.style.height = Math.round(initMobileW * 1.17) + 'px';
        poster.style.transform = 'none';
      } else {
        var initSizeData = SIZE_MAP[state.size];
        if (initSizeData) {
          poster.style.height = Math.round(600 * initSizeData.ratio) + 'px';
        }
      }

      var mapWrapper = $('sm-map-wrapper');
      if (mapWrapper) mapWrapper.style.width = MAP_WIDTHS[state.size] || '78%';

      // updateMapScale is the single source of truth for fonts & positioning
      updateMapScale();
    }

    // Nothing to load. A moon phase is pure maths plus one image, so there's no
    // d3, no celestial and no star-data files — which is the entire reason this
    // customiser starts instantly where the star map has to bootstrap a library.
    refreshMoon();

    wireControls();
    showPrefillStep(prefill);
    syncSkyToggles();
    initMobileHandlers();

    // Handle width-only resizes (ignore iOS keyboard-driven height changes)
    var lastWidth = window.innerWidth;
    window.addEventListener('resize', function() {
      var newWidth = window.innerWidth;
      if (newWidth !== lastWidth) {
        lastWidth = newWidth;
        setTimeout(updateMapScale, 100);
      }
    });

    // Final settling pass once fonts and assets are loaded
    setTimeout(updateMapScale, 1500);

    // Second-item banner. If the customer arrived from the interstitial, show
    // a persistent bar reminding them of the 25% discount they're earning.
    // The discount itself is a native Shopify automatic discount — this bar
    // is signal only, and clears when they navigate away with the item added
    // (see the post-add-to-cart logic in addToCart).
    initSecondItemBanner();
  }

  // Post-buy overlay — a single dark-blue full-screen surface that fades in
  // the moment the customer clicks Buy on the confirm modal, and holds the
  // scene through either:
  //   • add-to-cart → transition its content to the "add another" offer
  //     (first item), or
  //   • add-to-cart → navigate to /cart (second item, overlay persists as
  //     "Adding to your cart" until the page changes).
  //
  // Keeping one surface across both states means the editor never flashes
  // through the transition. The palette matches the confirm modal so the
  // customer's eye stays on the same blue field.

  var postBuyOverlayEl = null;

  function showPostBuyOverlay() {
    if (postBuyOverlayEl) return;
    postBuyOverlayEl = document.createElement('div');
    postBuyOverlayEl.className = 'tsa-pb-overlay';
    postBuyOverlayEl.innerHTML =
      '<div class="tsa-pb-content" data-state="processing">' +
        '<div class="tsa-pb-spinner" aria-hidden="true"></div>' +
        '<p class="tsa-pb-processing-text">Adding to your cart</p>' +
      '</div>';
    document.body.appendChild(postBuyOverlayEl);
    document.body.style.overflow = 'hidden';
    // Trigger fade-in on next frame
    requestAnimationFrame(function () {
      postBuyOverlayEl.classList.add('is-visible');
    });
  }

  function hidePostBuyOverlay() {
    if (!postBuyOverlayEl) return;
    var el = postBuyOverlayEl;
    postBuyOverlayEl = null;
    el.classList.remove('is-visible');
    document.body.style.overflow = '';
    setTimeout(function () { if (el && el.parentNode) el.parentNode.removeChild(el); }, 350);
  }

  function transitionPostBuyToOffer(cartUrl) {
    if (!postBuyOverlayEl) return;
    var content = postBuyOverlayEl.querySelector('.tsa-pb-content');
    if (!content) return;

    var starmapUrl = (window.MOON_CONFIG && window.MOON_CONFIG.starmap_url) || '/pages/star-map-customizer';
    var moonUrl    = (window.MOON_CONFIG && window.MOON_CONFIG.moon_url)    || '/pages/moon-phase-customizer';

    // Fade current content out, then swap and fade in — reads as a single
    // surface changing its message rather than a modal-over-modal stack.
    content.classList.add('is-swapping');
    setTimeout(function () {
      var starmapImg = (window.MOON_CONFIG && window.MOON_CONFIG.starmap_image) ||
        'https://cdn.shopify.com/s/files/1/0921/5387/2720/files/StarFrameTransparent.png?v=1790681291';
      var moonImg = (window.MOON_CONFIG && window.MOON_CONFIG.moon_image) ||
        'https://cdn.shopify.com/s/files/1/0921/5387/2720/files/MoonFramedTransparent.png?v=1790680972';

      content.dataset.state = 'offer';
      content.innerHTML =
        '<span class="tsa-pb-badge">COMPLETE YOUR STORY</span>' +
        '<h2 class="tsa-pb-title">' +
          'Add another design ' +
          '<span class="tsa-pb-title-italic">and save 25%</span>' +
        '</h2>' +
        '<div class="tsa-pb-choices">' +
          '<a class="tsa-pb-choice" href="' + starmapUrl + '" data-choose>' +
            '<span class="tsa-pb-choice-media">' +
              '<img src="' + starmapImg + '" alt="Star map framed print" loading="lazy">' +
            '</span>' +
            '<span class="tsa-pb-choice-title">Star Map</span>' +
            '<span class="tsa-pb-choice-cta">Design now &rarr;</span>' +
          '</a>' +
          '<a class="tsa-pb-choice" href="' + moonUrl + '" data-choose>' +
            '<span class="tsa-pb-choice-media">' +
              '<img src="' + moonImg + '" alt="Moon phase framed print" loading="lazy">' +
            '</span>' +
            '<span class="tsa-pb-choice-title">Moon Phase</span>' +
            '<span class="tsa-pb-choice-cta">Design now &rarr;</span>' +
          '</a>' +
        '</div>' +
        '<a class="tsa-pb-decline" href="' + cartUrl + '?updated=' + Date.now() + '" data-decline>' +
          'No thanks, take me to my cart' +
        '</a>';

      content.querySelectorAll('[data-choose]').forEach(function (a) {
        a.addEventListener('click', function () {
          try { sessionStorage.setItem('tsa_second_item_flow', '1'); } catch (e) {}
        });
      });
      content.querySelectorAll('[data-decline]').forEach(function (a) {
        a.addEventListener('click', function () {
          try { sessionStorage.removeItem('tsa_second_item_flow'); } catch (e) {}
        });
      });

      content.classList.remove('is-swapping');
    }, 250);
  }

  function initSecondItemBanner() {
    var isSecondItem = false;
    try { isSecondItem = sessionStorage.getItem('tsa_second_item_flow') === '1'; }
    catch (e) { return; }
    if (!isSecondItem) return;
    if (document.getElementById('tsa-second-item-banner')) return;

    var bar = document.createElement('div');
    bar.id = 'tsa-second-item-banner';
    bar.setAttribute('role', 'status');
    bar.innerHTML =
      '<span class="tsa-sib-text">25% off applied automatically at checkout</span>';
    document.body.appendChild(bar);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
    initShippingMessage();
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Save for later + Size/Frame guide + Resume
  //
  // Injects three secondary actions into the confirm modal: Size guide, Frame
  // guide, and Save for later. Save captures the customer's email, stores the
  // full design state on the Worker (which fires an Omnisend event to email
  // them a link), then a resume=xxx URL param on that link restores the design
  // when they return.
  // ─────────────────────────────────────────────────────────────────────────

  var PRODUCT_KIND = 'moon';

  // If arriving via a save-for-later resume link, inject a fullscreen loading
  // overlay right away so the customer never sees the empty customiser flash
  // through while state is being fetched and restored. Fades out when restore
  // completes (or fails), revealing the finished design.
  //
  // The Liquid section may inject its own overlay inline (fastest, no flash);
  // in that case we skip and just let this JS control the same node.
  (function injectResumeOverlayEarly() {
    try {
      if (window.location.search.indexOf('resume=') === -1) return;
      if (document.getElementById('tsa-resume-overlay')) return;
      var overlay = document.createElement('div');
      overlay.id = 'tsa-resume-overlay';
      overlay.innerHTML =
        '<div class="tsa-resume-inner">' +
          '<div class="tsa-resume-spinner"></div>' +
          '<p class="tsa-resume-text">Loading your design</p>' +
        '</div>';
      var attach = function () {
        if (document.getElementById('tsa-resume-overlay')) return;
        if (document.body) document.body.appendChild(overlay);
      };
      if (document.body) attach();
      else document.addEventListener('DOMContentLoaded', attach);
    } catch (e) {}
  })();

  function fadeOutResumeOverlay() {
    var overlay = document.getElementById('tsa-resume-overlay');
    if (!overlay) return;
    overlay.classList.add('is-fading');
    setTimeout(function () {
      if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
    }, 500);
  }

  function serialiseFullState() {
    var s = {};
    Object.keys(state).forEach(function (k) {
      var v = state[k];
      if (v instanceof Date) s[k] = v.toISOString();
      else s[k] = v;
    });
    return s;
  }

  // Capture the poster as a JPEG data URL for the save-for-later email preview.
  // Uses PosterSVG (the same renderer the editor + print use, so no drift) →
  // SVG string → Image → downscaled canvas → JPEG. A "PREVIEW" watermark is
  // baked in so the emailed image can't be repurposed as a print. Returns null
  // on any failure, so the save still succeeds without a preview.
  function capturePosterForEmail() {
    return new Promise(function (resolve) {
      try {
        if (!window.PosterSVG || !window.PosterSVG.renderPosterSVG) return resolve(null);
        // Use the local buildPosterSVG wrapper, not PosterSVG directly — it
        // sets the moon-specific options (noRing, slotImageHref, slotNoZoom,
        // slotNoClip, slotScale) that make the actual moon image appear.
        var svgMarkup = buildPosterSVG({ withSlot: true });
        if (!svgMarkup) return resolve(null);

        var img = new Image();
        var svgBlob = new Blob([svgMarkup], { type: 'image/svg+xml;charset=utf-8' });
        var url = URL.createObjectURL(svgBlob);

        var timeout = setTimeout(function () {
          URL.revokeObjectURL(url);
          resolve(null);
        }, 5000);

        img.onload = function () {
          clearTimeout(timeout);
          try {
            var TARGET_W = 600;
            var ratio = img.height / img.width;
            var canvas = document.createElement('canvas');
            canvas.width = TARGET_W;
            canvas.height = Math.round(TARGET_W * ratio);
            var ctx = canvas.getContext('2d');
            ctx.fillStyle = '#0a0a0a';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

            URL.revokeObjectURL(url);
            resolve(canvas.toDataURL('image/jpeg', 0.82));
          } catch (err) {
            URL.revokeObjectURL(url);
            resolve(null);
          }
        };
        img.onerror = function () {
          clearTimeout(timeout);
          URL.revokeObjectURL(url);
          resolve(null);
        };
        img.src = url;
      } catch (err) {
        resolve(null);
      }
    });
  }

  // Save for later — small centred text link on step 4, sits just above the
  // Back / Add to Cart nav row. Placed here (not on the confirm modal) so the
  // customer sees the option BEFORE clicking Add to Cart, avoiding the "I'm
  // not ready to enter card details" hesitation.
  function injectStep4SaveLink() {
    var step4 = $('sm-step-4');
    if (!step4) return;
    if (step4.querySelector('[data-tsa-step4-save]')) return;
    var nav = step4.querySelector('.sm-step-nav');
    if (!nav) return;

    var wrap = document.createElement('div');
    wrap.setAttribute('data-tsa-step4-save', '');
    wrap.className = 'tsa-save-link-wrap';
    wrap.innerHTML = '<button type="button" class="tsa-save-link">Not ready? Save this design for later</button>';

    nav.parentNode.insertBefore(wrap, nav);
    wrap.querySelector('button').addEventListener('click', showSaveModal);
  }

  // Size guide — button injected right-aligned inside the "SIZE" label so it
  // sits on the same line. Image shown depends on selected format.
  function injectSizeGuideButton() {
    injectGuideLink('.sm-size-row', 'size', 'See size guide');
  }

  // Frame guide — same pattern, injected into the "FRAME COLOR" label. The
  // frame colour field itself is auto-hidden by updateStep4UI when the format
  // doesn't have a frame, so this link inherits that visibility.
  function injectFrameGuideButton() {
    var frameField = $('sm-frame-colour-field');
    if (!frameField) return;
    var swatchRow = frameField.querySelector('.sm-swatch-row');
    if (swatchRow) injectGuideLink(swatchRow, 'frame', 'See frame guide');
  }

  function injectGuideLink(rowEl, kind, label) {
    var row = typeof rowEl === 'string' ? document.querySelector(rowEl) : rowEl;
    if (!row) return;
    var field = row.closest('.sm-field');
    if (!field) return;
    var labelEl = field.querySelector(':scope > label');
    if (!labelEl) return;
    if (labelEl.querySelector('[data-tsa-guide]')) return;

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'tsa-guide-link';
    btn.setAttribute('data-tsa-guide', kind);
    btn.innerHTML = label;
    btn.addEventListener('click', function () { showGuideModal(kind); });

    labelEl.classList.add('sm-label-with-guide');
    labelEl.appendChild(btn);
  }

  // Preload guide images at browser-idle time with low fetch priority. The
  // browser only starts these downloads once higher-priority work (page render,
  // autocomplete API calls, etc.) has settled, so this never competes with
  // step-1 location typing — but by the time the customer reaches step 4, the
  // images are already cached and the modal opens instantly.
  var guideImagesPreloaded = false;
  function preloadGuideImages() {
    if (guideImagesPreloaded) return;
    guideImagesPreloaded = true;
    var cfg = window.MOON_CONFIG || {};
    ['size_guide_poster_url', 'size_guide_canvas_url', 'frame_guide_wooden_url', 'frame_guide_canvas_url'].forEach(function (k) {
      if (!cfg[k]) return;
      var img = new Image();
      if ('fetchPriority' in img) img.fetchPriority = 'low';
      img.src = cfg[k];
    });
  }

  function armGuidePreload() {
    // Prefer requestIdleCallback so we only run when the browser has nothing
    // better to do. Fallback to a delayed setTimeout otherwise.
    var kick = function () { preloadGuideImages(); };
    if ('requestIdleCallback' in window) {
      requestIdleCallback(kick, { timeout: 5000 });
    } else {
      setTimeout(kick, 3000);
    }
  }

  function showGuideModal(kind) {
    var cfg = window.MOON_CONFIG || {};
    var isCanvas = isCanvasFormat(state.format);
    var url;

    if (kind === 'frame') {
      url = isCanvas ? cfg.frame_guide_canvas_url : cfg.frame_guide_wooden_url;
    } else {
      url = isCanvas ? cfg.size_guide_canvas_url : cfg.size_guide_poster_url;
    }

    if (!url) {
      alert('Guide is not available yet.');
      return;
    }

    var overlay = document.createElement('div');
    overlay.className = 'tsa-guide-overlay';
    overlay.innerHTML =
      '<button type="button" class="tsa-guide-close" aria-label="Close">' +
        '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">' +
          '<line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>' +
        '</svg>' +
      '</button>' +
      '<img class="tsa-guide-img" src="' + url + '" alt="Guide">';
    document.body.appendChild(overlay);
    document.body.style.overflow = 'hidden';

    var close = function () {
      overlay.classList.remove('is-visible');
      setTimeout(function () {
        if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
        document.body.style.overflow = '';
      }, 250);
    };
    overlay.querySelector('.tsa-guide-close').addEventListener('click', close);
    overlay.addEventListener('click', function (e) { if (e.target === overlay) close(); });

    requestAnimationFrame(function () { overlay.classList.add('is-visible'); });
  }

  function showSaveModal() {
    var overlay = document.createElement('div');
    overlay.className = 'tsa-save-overlay';
    overlay.innerHTML =
      '<div class="tsa-save-box" role="dialog" aria-label="Save for later">' +
        '<button type="button" class="tsa-save-close" aria-label="Close">&times;</button>' +
        '<div class="tsa-save-body" data-state="form">' +
          '<h3 class="tsa-save-title">Save this design for later</h3>' +
          '<p class="tsa-save-sub">Enter your email and we\'ll send you a link so you can come back to this design whenever you\'re ready.</p>' +
          '<label class="tsa-save-label">Email address</label>' +
          '<input type="email" inputmode="email" autocomplete="email" class="tsa-save-input" placeholder="you@email.com" data-save-email>' +
          '<label class="tsa-save-check">' +
            '<input type="checkbox" data-save-consent>' +
            '<span>I agree to receive marketing emails from The Stars Above. <strong>(required)</strong></span>' +
          '</label>' +
          '<p class="tsa-save-err" data-save-err hidden></p>' +
          '<button type="button" class="tsa-save-submit" data-save-submit>Send me the link</button>' +
          '<p class="tsa-save-note">Your link expires in 3 days.</p>' +
        '</div>' +
      '</div>';
    document.body.appendChild(overlay);
    document.body.style.overflow = 'hidden';

    var close = function () {
      overlay.classList.remove('is-visible');
      setTimeout(function () {
        if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
        document.body.style.overflow = '';
      }, 250);
    };
    overlay.querySelector('.tsa-save-close').addEventListener('click', close);
    overlay.addEventListener('click', function (e) { if (e.target === overlay) close(); });

    var submitBtn = overlay.querySelector('[data-save-submit]');
    submitBtn.addEventListener('click', function () {
      var emailEl = overlay.querySelector('[data-save-email]');
      var consentEl = overlay.querySelector('[data-save-consent]');
      var errEl = overlay.querySelector('[data-save-err]');
      var email = (emailEl.value || '').trim();
      var showErr = function (m) { errEl.textContent = m; errEl.hidden = !m; };

      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return showErr('Please enter a valid email address.');
      if (!consentEl.checked) return showErr('Please tick the box to agree to marketing emails.');
      showErr('');

      submitBtn.disabled = true;
      var orig = submitBtn.textContent;
      submitBtn.textContent = 'Sending…';

      capturePosterForEmail().then(function (previewData) {
        return fetch(WORKER_URL + '/api/save', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: email,
            consent: true,
            product: PRODUCT_KIND,
            state: serialiseFullState(),
            previewData: previewData,
            heading: state.name || ''
          })
        });
      })
        .then(function (r) {
          if (!r.ok) return r.json().then(function (b) { throw new Error(b.error || 'Save failed'); });
          return r.json();
        })
        .then(function () {
          var body = overlay.querySelector('.tsa-save-body');
          body.dataset.state = 'done';
          body.innerHTML =
            '<div class="tsa-save-tick" aria-hidden="true">' +
              '<svg viewBox="0 0 44 44" width="44" height="44">' +
                '<circle cx="22" cy="22" r="21" fill="none" stroke="currentColor" stroke-width="1.5" opacity="0.35"/>' +
                '<path d="M13 22.5l6 6 12-13" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/>' +
              '</svg>' +
            '</div>' +
            '<h3 class="tsa-save-title">Your design is saved</h3>' +
            '<p class="tsa-save-sub">We\'ve emailed you a link. If it hasn\'t arrived in a few minutes, please check your spam folder.</p>' +
            '<button type="button" class="tsa-save-submit" data-save-done>Back to my design</button>';
          body.querySelector('[data-save-done]').addEventListener('click', close);
        })
        .catch(function (err) {
          submitBtn.disabled = false;
          submitBtn.textContent = orig;
          showErr(err.message || 'Something went wrong. Please try again.');
        });
    });

    requestAnimationFrame(function () { overlay.classList.add('is-visible'); });
  }

  function tryResumeSavedDesign() {
    var params = new URLSearchParams(window.location.search);
    var token = params.get('resume');
    if (!token || !/^[0-9a-f]{32}$/.test(token)) {
      // Nothing to resume — clear any overlay that was speculatively shown.
      fadeOutResumeOverlay();
      return;
    }

    fetch(WORKER_URL + '/api/save/' + token)
      .then(function (r) {
        if (!r.ok) return r.json().then(function (b) { throw new Error(b.error || 'Not found'); });
        return r.json();
      })
      .then(function (data) {
        if (!data.saved || !data.saved.state) throw new Error('Invalid saved data');
        restoreSavedDesign(data.saved.state);
        // Jump straight to step 4 — the customer has already made every choice
        // this design needs.
        jumpToStep(4);
        // Give the moon canvas + step 4 layout ~600ms to settle before the
        // overlay fades out. Prevents the "loading → half-rendered design → done"
        // flash and makes the reveal feel intentional.
        setTimeout(fadeOutResumeOverlay, 600);
      })
      .catch(function (err) {
        console.warn('Resume failed:', err && err.message);
        fadeOutResumeOverlay();
      });
  }

  function restoreSavedDesign(saved) {
    // Overwrite state fields. Date arrives as ISO string; rehydrate.
    Object.keys(saved).forEach(function (k) {
      var v = saved[k];
      if (k === 'date' && typeof v === 'string') state.date = new Date(v);
      else state[k] = v;
    });

    // Populate the main text inputs. Size/format/frame buttons are handled by
    // their own state → UI wiring inside the customiser, so calling refreshMoon
    // + updateMapScale below is what makes the preview come back correct.
    var setInput = function (id, val) {
      var el = $(id);
      if (el && val != null) el.value = val;
    };
    setInput('sm-name-input', state.name);
    setInput('sm-message-input', state.message);
    setInput('sm-location', state.locationName);

    // Show the font rows for any restored text, with the saved fonts highlighted.
    syncFontButtons();
    syncFontFields();

    // Date/time inputs.
    if (state.date instanceof Date && !isNaN(state.date)) {
      var d = state.date;
      var pad = function (n) { return n < 10 ? '0' + n : '' + n; };
      var dateEl = $('sm-date');
      var timeEl = $('sm-time');
      if (dateEl) dateEl.value = d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());
      if (timeEl) timeEl.value = pad(d.getHours()) + ':' + pad(d.getMinutes());
    }

    // Refresh the preview.
    if (typeof refreshMoon === 'function') refreshMoon();
    if (typeof updateMapScale === 'function') updateMapScale();
  }

  // Mirror of the wizard's own goToStep function (defined in the section's
  // inline script and not accessible from here). Manipulates the same classes
  // so the step indicator, panel visibility, and scroll all match a manual
  // Next click.
  function jumpToStep(n) {
    document.querySelectorAll('.sm-step-panel').forEach(function (p) {
      p.classList.add('sm-step-hidden');
    });
    document.querySelectorAll('.sm-step-item').forEach(function (item) {
      var s = parseInt(item.dataset.step, 10);
      item.classList.remove('sm-step-active', 'sm-step-done');
      if (s === n) item.classList.add('sm-step-active');
      if (s < n) item.classList.add('sm-step-done');
    });
    var panel = $('sm-step-' + n);
    if (panel) panel.classList.remove('sm-step-hidden');
    var controls = document.querySelector('.sm-controls');
    if (controls) controls.scrollTop = 0;
  }

  function showResumedToast() {
    var toast = document.createElement('div');
    toast.className = 'tsa-resumed-toast';
    toast.innerHTML =
      '<span class="tsa-resumed-tick" aria-hidden="true">' +
        '<svg viewBox="0 0 22 22" width="16" height="16">' +
          '<path d="M6 11.5l3 3 7-7.5" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>' +
        '</svg>' +
      '</span>' +
      '<span>Saved design loaded</span>';
    document.body.appendChild(toast);
    requestAnimationFrame(function () { toast.classList.add('is-visible'); });
    setTimeout(function () {
      toast.classList.remove('is-visible');
      setTimeout(function () { if (toast.parentNode) toast.parentNode.removeChild(toast); }, 400);
    }, 4000);
  }

  // Kick off both features. Runs on next tick so the confirm modal DOM is
  // definitely present. Resume runs immediately so state is set before the
  // customer starts interacting.
  setTimeout(function () {
    injectStep4SaveLink();
    injectSizeGuideButton();
    injectFrameGuideButton();
    armGuidePreload();
    tryResumeSavedDesign();
  }, 0);

  // Expose minimal public API for debugging
  window.MoonCustomiser = { state: state, refreshMoon: refreshMoon, updateMapScale: updateMapScale, uploadToWorker: uploadToWorker };
})();